import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from "react";
import { generateObservationReportPDF } from "../../../../../utils/generateObservationReportPDF";
import {
  Clock,
  CheckCircle,
  Send,
  Wrench,
  CircleAlert,
  ArrowLeft,
  Eye,
  Upload,
  Download,
  X as XIcon,
  Plus,
  ClipboardList,
  TriangleAlert,
  Calendar,
  Camera,
  UploadCloud,
  UserRound,
  FileText,
  ShieldCheck,
  Building2,
  UserCheck,
} from "lucide-react";
import { SafetyHeroBanner } from "../../../SafetyBannerArt";
import SafetyResponsiveSignatureCanvas from "../../../SafetyResponsiveSignatureCanvas";

import {
  getSafetyChecklist,
  getSafetyTemplate,
  listSafetyCategories,
  listSafetyChecklists,
  listSafetyTemplates,
  listSafetyProjectReportConfigs,
  resolveActiveProjectId,
  resolveOrgId,
  submitSafetyChecklist,
  createAndSubmitSafetyChecklist,
  listContractorNamesByOrg,
  downloadSafetyReport,
  listSafetyObservations,
  fetchAllSafetyObservations,
  getSafetyObservationById,
  getSafetyObservationCheckers,
  claimSafetyObservation,
  submitSafetyObservationRectification,
  downloadSafetyObservationReportById,
  downloadSafetyObservationReport,
  downloadBlob,
  filenameFromDisposition,
} from "../../../../../api";
import { showToast } from "../../../../../utils/toast";
import { getCurrentUserId } from "../../../../../utils/UserUtils";
import {
  groupApprovedObservationsByReportBatch,
  groupObservationsByReportBatch,
  formatReportDisplayDate,
} from "../../../../../utils/safetyObservationReportUtils";

import SafetyChecklistHistoryModal from "../../SafetyChecklistHistoryModal";
import SafetyChecklistReadonlyModal from "../../SafetyChecklistReadonlyModal";
import SafetyObservationReadonlyModal from "../../SafetyObservationReadonlyModal";

import SafetyChecklistSignOffSection from "../../SafetyChecklistSignOffSection";
import SafetyObservationSignOffSection from "../../SafetyObservationSignOffSection";

import SafetyImageAnnotationModal from "../../SafetyImageAnnotationModal";
import SafetyCameraCaptureModal from "../../SafetyCameraCaptureModal";
import RiskMatrixModal from "./RiskMatrixModal";
import { getReportNumberPreview } from "../../../shared/safetyHeaderUtils";
import {
  isObservationMakerActionable,
  isObservationVisibleToMaker,
  getInternalOrganizationName,
} from "./observationVisibilityHelpers";

const MetaChip = ({ icon, label, value, tone = "slate" }) => {
  const toneClasses = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    purple: "bg-purple-50 border-purple-200 text-purple-700",
  }[tone];

  return (
    <span
      className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${toneClasses}`}
    >
      {icon}
      {label && <span className="text-muted-foreground/80">{label}</span>}
      <span className="truncate font-semibold">{value}</span>
    </span>
  );
};

const MAKER_QUEUE_STATUSES = [
  "Pending for Maker",
  "rejected_by_checker",
  "rejected_by_supervisor",
  "created",
];

function collectActiveMakerSubs(detail) {
  const out = [];
  (detail?.items || []).forEach((item) => {
    const sub = (item.submissions || []).find((s) =>
      MAKER_QUEUE_STATUSES.includes(s.status),
    );
    if (sub) out.push(sub);
  });
  return out;
}

function isRejectReworkStatus(st) {
  return st === "rejected_by_checker" || st === "rejected_by_supervisor";
}

/** First full forward submit (no reject rework): this request clears every maker-queue submission. */
function shouldShowMakerSignatureModal(
  detail,
  submissionsForApi,
  isBulkSubmit,
) {
  const makerSubs = collectActiveMakerSubs(detail);
  if (!makerSubs.length) return false;
  if (makerSubs.some((s) => isRejectReworkStatus(s.status))) return false;

  if (isBulkSubmit) return true;

  const ids = new Set(makerSubs.map((s) => s.id));
  const touched = new Set(
    (submissionsForApi || []).map((p) => p.submission_id).filter(Boolean),
  );
  if (ids.size !== touched.size) return false;
  for (const id of ids) {
    if (!touched.has(id)) return false;
  }
  return true;
}

// ─── Shared inline UI helpers ────────────────────────────────
const QuestionBadge = ({ number }) => (
  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-xs font-bold text-white shadow-sm">
    {number}
  </span>
);

const decodeJwtPayload = (token) => {
  try {
    if (!token || typeof token !== "string") return null;

    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(""),
    );

    return JSON.parse(json);
  } catch {
    return null;
  }
};

const getCurrentUserProfile = () => {
  let fallbackProfile = null;

  // 1. First try known user object keys
  const objectKeys = [
    "USER_DATA",
    "user",
    "currentUser",
    "userData",
    "authUser",
  ];

  for (const key of objectKeys) {
    try {
      const raw = localStorage.getItem(key);

      if (!raw || raw === "undefined" || raw === "null") continue;

      const parsed = JSON.parse(raw);

      if (parsed?.user_type) {
        return parsed;
      }

      if (!fallbackProfile && (parsed?.user_id || parsed?.id)) {
        fallbackProfile = parsed;
      }
    } catch {
      // ignore and continue
    }
  }

  // 2. Try known token keys
  const tokenKeys = [
    "ACCESS_TOKEN",
    "access",
    "access_token",
    "accessToken",
    "token",
    "authToken",
  ];

  for (const key of tokenKeys) {
    const token = localStorage.getItem(key);
    const decoded = decodeJwtPayload(token);

    if (decoded?.user_type || decoded?.user_id) {
      return fallbackProfile ? { ...fallbackProfile, ...decoded } : decoded;
    }
  }

  // 3. Last fallback: scan localStorage for any JWT that contains user_type
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    const value = localStorage.getItem(key);

    const decoded = decodeJwtPayload(value);

    if (decoded?.user_type || decoded?.user_id) {
      return fallbackProfile ? { ...fallbackProfile, ...decoded } : decoded;
    }
  }

  return fallbackProfile || {};
};

export const autoResizeTextarea = (el) => {
  if (!el) return;

  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

const resolveMakerContext = () => {
  const profile = getCurrentUserProfile() || {};

  const accesses = Array.isArray(profile?.accesses) ? profile.accesses : [];

  const activeAccess =
    accesses.find((a) => a?.active && a?.project_id) ||
    accesses.find((a) => a?.project_id) ||
    null;

  const resolvedProjectId = String(
    resolveActiveProjectId?.() ||
    localStorage.getItem("ACTIVE_PROJECT_ID") ||
    localStorage.getItem("active_project_id") ||
    localStorage.getItem("PROJECT_ID") ||
    localStorage.getItem("project_id") ||
    activeAccess?.project_id ||
    profile?.project_id ||
    "",
  ).trim();

  const resolvedOrgId = String(
    resolveOrgId?.() ||
    localStorage.getItem("ORG_ID") ||
    localStorage.getItem("org_id") ||
    localStorage.getItem("organization_id") ||
    profile?.org ||
    profile?.org_id ||
    profile?.organization_id ||
    profile?.organization?.id ||
    "",
  ).trim();

  // Keep storage ready so existing api helpers also work.
  if (resolvedProjectId) {
    localStorage.setItem("ACTIVE_PROJECT_ID", resolvedProjectId);
    localStorage.setItem("PROJECT_ID", resolvedProjectId);
  }

  if (resolvedOrgId) {
    localStorage.setItem("ORG_ID", resolvedOrgId);
    localStorage.setItem("org_id", resolvedOrgId);
  }

  return {
    projectId: resolvedProjectId,
    orgId: resolvedOrgId,
    profile,
  };
};

export const resolveMediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const base =
    window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "localhost"
      ? "http://127.0.0.1:8001"
      : "https://konstruct.world/checklists";

  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${base}${clean}`;
};

const PhotoViewButton = ({ url, label = "View Photo" }) => {
  const finalUrl = resolveMediaUrl(url);
  if (!finalUrl) return null;

  return (
    <a
      href={finalUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 hover:border-blue-300"
    >
      <Eye className="h-3.5 w-3.5" />
      {label}
    </a>
  );
};

const renderObservationHazards = (hazardStr) => {
  if (!hazardStr) return <p className="text-sm text-foreground">-</p>;
  try {
    const arr =
      typeof hazardStr === "string" ? JSON.parse(hazardStr) : hazardStr;
    if (Array.isArray(arr) && arr.length > 0) {
      return (
        <div className="flex flex-wrap gap-2">
          {arr.map((h, i) => (
            <span
              key={i}
              className="inline-flex items-center rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700"
            >
              {h}
            </span>
          ))}
        </div>
      );
    }
  } catch (e) {
    // fallback
  }
  return <p className="text-sm text-foreground">{String(hazardStr)}</p>;
};

const renderObservationRisk = (risk) => {
  if (!risk) return <p className="text-sm text-foreground">-</p>;

  const cleanRisk = String(risk).trim();
  const isLow = cleanRisk.toLowerCase() === "low risk";
  const isMedium = cleanRisk.toLowerCase() === "medium risk";
  const isHigh = cleanRisk.toLowerCase() === "high risk";

  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${isLow
          ? "border border-green-200 bg-green-50 text-green-700"
          : isMedium
            ? "border border-yellow-200 bg-yellow-50 text-yellow-700"
            : isHigh
              ? "border border-red-200 bg-red-50 text-red-700"
              : "border border-border bg-muted/30 text-foreground"
        }`}
    >
      {cleanRisk}
    </span>
  );
};

const renderNumberedActionList = (text) => {
  if (!text || !String(text).trim()) {
    return <p className="text-sm text-muted-foreground italic">-</p>;
  }

  const raw = String(text).trim();
  const splitRegex = /\r?\n|(?<=[^,\s])\s*,\s*(?=\d+[\.\)])|(?<=[^\d\n])\s+(?=\d+[\.\)])/;
  const items = raw
    .split(splitRegex)
    .map((item) => item.trim())
    .filter(Boolean);

  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground italic">-</p>;
  }

  const anyHasNumbering = items.some((item) => /^\d+[\.\)]/.test(item));

  return (
    <div className="mt-1 space-y-1">
      {items.map((item, idx) => {
        const displayLine =
          anyHasNumbering || items.length === 1
            ? item
            : `${idx + 1}. ${item}`;
        return (
          <p key={idx} className="text-sm text-foreground leading-relaxed">
            {displayLine}
          </p>
        );
      })}
    </div>
  );
};

const getObservationPhotoUrl = (photo) => {
  if (!photo) return null;
  if (typeof photo === "string") return resolveMediaUrl(photo);
  if (typeof photo === "object") {
    const candidate =
      photo.annotated_image_url ||
      photo.annotated_image ||
      photo.image_url ||
      photo.image ||
      photo.url ||
      photo.path;
    if (candidate) return resolveMediaUrl(candidate);
  }
  return null;
};

const formatObservationInspectionDate = (dateVal) => {
  if (!dateVal) {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = now.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }
  try {
    const raw = String(dateVal).trim();
    const datePart = raw.split("T")[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [y, m, d] = datePart.split("-");
      return `${d}-${m}-${y}`;
    }
    const d = new Date(dateVal);
    if (!Number.isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    }
    return String(dateVal);
  } catch {
    return String(dateVal);
  }
};

const formatObservationTargetDate = (dateVal) => {
  if (!dateVal) return "-";
  try {
    const raw = String(dateVal).trim();
    if (!raw) return "-";
    const parts = raw.split("T");
    const datePart = parts[0];
    const timePart = parts[1] ? parts[1].substring(0, 5) : "";

    let formattedDate = datePart;
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [y, m, d] = datePart.split("-");
      formattedDate = `${d}-${m}-${y}`;
    } else {
      const d = new Date(dateVal);
      if (!Number.isNaN(d.getTime())) {
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        formattedDate = `${day}-${month}-${year}`;
      }
    }

    if (timePart) {
      const [hStr, mStr] = timePart.split(":");
      const hNum = parseInt(hStr, 10);
      const mNum = mStr !== undefined ? mStr.substring(0, 2) : "00";
      if (!Number.isNaN(hNum)) {
        const period = hNum >= 12 ? "PM" : "AM";
        const h12 = hNum % 12 || 12;
        return `${formattedDate} (${h12}:${mNum} ${period})`;
      }
      return `${formattedDate} (${timePart})`;
    }
    return formattedDate;
  } catch {
    return String(dateVal);
  }
};

const NumberedTextArea = ({
  name,
  value = "",
  onChange,
  placeholder,
  required,
  rows = 3,
  className = "",
  testId,
}) => {
  const textareaRef = useRef(null);

  const onValChange = (e) => {
    let val = e.target.value;
    if (val.length === 1 && (value === "" || value === undefined || value === null)) {
      val = "1. " + val;
    }
    if (onChange) {
      onChange({ target: { name, value: val } });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const el = textareaRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const val = el.value || "";

      const textBefore = val.substring(0, start);
      const textAfter = val.substring(end);

      const lines = textBefore.split("\n");
      const nextNum = lines.length + 1;
      const insertText = `\n${nextNum}. `;

      const newValue = textBefore + insertText + textAfter;

      if (onChange) {
        onChange({ target: { name, value: newValue } });
      }

      setTimeout(() => {
        if (el) {
          el.selectionStart = el.selectionEnd = start + insertText.length;
        }
      }, 0);
    }
  };

  return (
    <textarea
      ref={textareaRef}
      rows={rows}
      name={name}
      value={value}
      onChange={onValChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={className}
      required={required}
      data-testid={testId}
    />
  );
};

// const PhotoUploadArea = ({
//     id,
//     previewBase64,
//     onFileChange,
//     onRemove,
//     label = "Attach photo",
// }) => {
//     const inputRef = useRef(null);
//     const previewUrl = previewBase64
//         ? typeof previewBase64 === "string"
//             ? resolveMediaUrl(previewBase64)
//             : URL.createObjectURL(previewBase64)
//         : null;

//     const handleRemove = () => {
//         if (inputRef.current) {
//             inputRef.current.value = "";
//         }
//         onRemove?.();
//     };

//     return (
//         <div className="mt-3">
//             <p className="mb-1.5 text-xs font-medium text-muted-foreground">{label}</p>

//             {previewUrl ? (
//                 <div className="relative inline-block">
//                     <img
//                         src={previewUrl}
//                         alt="Preview"
//                         className="h-20 w-20 rounded-lg border border-border object-cover shadow-sm"
//                     />
//                     <button
//                         type="button"
//                         onClick={handleRemove}
//                         className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow transition-colors hover:bg-red-600"
//                         aria-label="Remove uploaded photo"
//                     >
//                         <XIcon className="h-3 w-3" />
//                     </button>
//                 </div>
//             ) : (
//                 <label
//                     htmlFor={`photo-upload-${id}`}
//                     className="flex cursor-pointer items-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/60"
//                 >
//                     <Upload className="h-4 w-4" />
//                     <span>Click to upload or take a photo</span>
//                 </label>
//             )}

//             <input
//                 ref={inputRef}
//                 id={`photo-upload-${id}`}
//                 type="file"
//                 accept="image/*"
//                 onChange={onFileChange}
//                 className="hidden"
//             />
//         </div>
//     );
// };

const PhotoUploadArea = ({
  id,
  previewBase64,
  onFileChange,
  onRemove,
  label = "Attach photo",
}) => {
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [annotationFile, setAnnotationFile] = useState(null);
  const [annotationOpen, setAnnotationOpen] = useState(false);

  const previewUrl = previewBase64
    ? typeof previewBase64 === "string"
      ? resolveMediaUrl(previewBase64)
      : URL.createObjectURL(previewBase64)
    : null;

  const handleInputChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!String(file.type || "").startsWith("image/")) {
      showToast("Please upload a valid image file.", "error");

      if (galleryInputRef.current) galleryInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";

      return;
    }

    setAnnotationFile(file);
    setAnnotationOpen(true);
  };

  const handleAnnotatedSave = (annotatedFile) => {
    setAnnotationOpen(false);
    setAnnotationFile(null);

    /*
      Keep existing parent usage working.
      Parent code already expects e.target.files[0].
    */
    onFileChange?.({
      target: {
        files: [annotatedFile],
      },
    });

    if (galleryInputRef.current) galleryInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const handleAnnotationClose = () => {
    setAnnotationOpen(false);
    setAnnotationFile(null);

    if (galleryInputRef.current) galleryInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
  };

  const handleRemove = () => {
    if (galleryInputRef.current) galleryInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";

    setAnnotationOpen(false);
    setAnnotationFile(null);

    onRemove?.();
  };

  return (
    <div className="mt-3">
      {previewUrl ? (
        <div>
          {label && (
            <p className="mb-1.5 text-xs font-medium text-muted-foreground">
              {label}
            </p>
          )}
          <div className="relative inline-block">
            <img
              src={previewUrl}
              alt="Preview"
              className="h-20 w-20 rounded-lg border border-border object-cover shadow-sm"
            />

            <button
              type="button"
              onClick={handleRemove}
              className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow transition-colors hover:bg-red-600"
              aria-label="Remove uploaded photo"
            >
              <XIcon className="h-3 w-3" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-orange-200 bg-orange-50/20 px-4 py-5 text-center transition-colors hover:bg-orange-50/40">
          <UploadCloud className="mb-2 h-7 w-7 text-orange-400" />
          <p className="mb-1 text-xs sm:text-sm font-semibold text-slate-700">
            {label || "Attach photo"}
          </p>
          <p className="mb-3 text-[11px] text-slate-400">PNG, JPG, JPEG</p>
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            <label
              htmlFor={`photo-upload-gallery-${id}`}
              className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3.5 py-2 text-xs font-semibold text-orange-700 hover:bg-orange-100 transition-colors shadow-sm"
            >
              <UploadCloud className="h-4 w-4 text-orange-600" />
              <span>Select from Gallery</span>
            </label>

            <button
              type="button"
              onClick={() => setCameraOpen(true)}
              className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-orange-600 px-3.5 py-2 text-xs font-semibold text-white hover:bg-orange-700 transition-colors shadow-sm"
            >
              <Camera className="h-4 w-4" />
              <span>Take Photo</span>
            </button>
          </div>
        </div>
      )}

      <input
        ref={galleryInputRef}
        id={`photo-upload-gallery-${id}`}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        className="hidden"
      />

      <input
        ref={cameraInputRef}
        id={`photo-upload-camera-${id}`}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleInputChange}
        className="hidden"
      />

      <SafetyCameraCaptureModal
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={(capturedFile) => {
          if (!capturedFile) return;
          setAnnotationFile(capturedFile);
          setAnnotationOpen(true);
        }}
        title="Take Photo"
      />

      <SafetyImageAnnotationModal
        open={annotationOpen}
        file={annotationFile}
        onClose={handleAnnotationClose}
        onSave={handleAnnotatedSave}
      />
    </div>
  );
};

const getOptionToneByIndex = (index) => {
  if (index === 0) {
    return {
      active: "border-green-500 bg-green-500 text-white shadow-sm",
      idle: "border-green-200 bg-green-50 text-green-700 hover:bg-green-100",
    };
  }

  if (index === 1) {
    return {
      active: "border-red-500 bg-red-500 text-white shadow-sm",
      idle: "border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
    };
  }

  if (index === 2) {
    return {
      active: "border-yellow-500 bg-yellow-500 text-white shadow-sm",
      idle: "border-yellow-200 bg-yellow-50 text-yellow-700 hover:bg-yellow-100",
    };
  }

  return {
    active: "border-slate-500 bg-slate-500 text-white shadow-sm",
    idle: "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100",
  };
};

export const TemplateOptionButtons = ({ options = [], value, onChange }) => {
  const normalizedOptions =
    Array.isArray(options) && options.length ? options : ["Yes", "No", "N/A"];

  return (
    <div className="flex flex-wrap gap-2">
      {normalizedOptions.map((opt, index) => {
        const label =
          typeof opt === "string"
            ? opt
            : opt?.label || opt?.name || opt?.title || "";

        if (!label) return null;

        const active =
          String(value || "")
            .trim()
            .toLowerCase() ===
          String(label || "")
            .trim()
            .toLowerCase();

        const toneClasses = getOptionToneByIndex(index);

        return (
          <button
            key={`${label}-${index}`}
            type="button"
            onClick={() => onChange(label)}
            className={`rounded-lg border px-4 py-2 text-sm font-semibold transition ${active ? toneClasses.active : toneClasses.idle
              }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};

export const DynamicSafetyInput = ({
  item,
  value,
  secondaryValue,
  onChange,
  onSecondaryChange,
}) => {
  const inputType = item.input_type || item.type || "multiple_choice";

  // Normalize options
  const options =
    Array.isArray(item.options) && item.options.length
      ? item.options
      : ["Yes", "No", "N/A"];
  const secondaryOptions =
    Array.isArray(item.secondary_options) && item.secondary_options.length
      ? item.secondary_options
      : [];

  const renderPrimaryInput = () => {
    switch (inputType) {
      case "short_answer":
        return (
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full rounded-lg border-gray-300 p-2.5 text-sm shadow-sm focus:border-orange-500 focus:ring-orange-500"
            placeholder="Enter your answer..."
          />
        );
      case "paragraph":
        return (
          <textarea
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            className="w-full rounded-lg border-gray-300 p-2.5 text-sm shadow-sm focus:border-orange-500 focus:ring-orange-500"
            placeholder="Enter detailed answer..."
          />
        );
      case "date":
        return (
          <input
            type="date"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full md:w-auto rounded-lg border-gray-300 p-2.5 text-sm shadow-sm focus:border-orange-500 focus:ring-orange-500"
          />
        );
      case "dropdown":
        return (
          <select
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full rounded-lg border-gray-300 p-2.5 text-sm shadow-sm focus:border-orange-500 focus:ring-orange-500 bg-white"
          >
            <option value="">Select an option</option>
            {options.map((opt, idx) => (
              <option key={idx} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );
      case "checkbox":
        const selectedList = value
          ? typeof value === "string"
            ? value.split(",").map((s) => s.trim())
            : value
          : [];
        return (
          <div className="flex flex-col gap-2 mt-2">
            {options.map((opt, idx) => {
              const isChecked = selectedList.includes(opt);
              return (
                <label
                  key={idx}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      const newSet = new Set(selectedList);
                      if (e.target.checked) newSet.add(opt);
                      else newSet.delete(opt);
                      onChange(Array.from(newSet).join(", "));
                    }}
                    className="h-5 w-5 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">{opt}</span>
                </label>
              );
            })}
          </div>
        );
      case "multiple_choice":
      default:
        if (inputType === "file_upload") {
          // file_upload is handled separately via the photo upload UI, but we can still show a placeholder or let photo upload area be the main thing
          return (
            <div className="text-sm text-gray-500 italic px-2 py-1 bg-gray-50 rounded-lg border border-dashed border-gray-300">
              Please use the photo attachment below.
            </div>
          );
        }
        return (
          <TemplateOptionButtons
            options={options}
            value={value}
            onChange={onChange}
          />
        );
    }
  };

  const renderSecondaryInput = () => {
    if (!item.has_secondary || !item.secondary_type) return null;

    return (
      <div className="mt-3 pl-4 border-l-2 border-orange-200">
        <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
          Enter Answer
        </p>
        {(() => {
          switch (item.secondary_type) {
            case "short_answer":
              return (
                <input
                  type="text"
                  value={secondaryValue || ""}
                  onChange={(e) => onSecondaryChange(e.target.value)}
                  className="w-full rounded-lg border-gray-300 p-2 text-sm shadow-sm focus:border-orange-500 focus:ring-orange-500 bg-gray-50"
                  placeholder="Enter answer..."
                />
              );
            case "paragraph":
              return (
                <textarea
                  value={secondaryValue || ""}
                  onChange={(e) => onSecondaryChange(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border-gray-300 p-2 text-sm shadow-sm focus:border-orange-500 focus:ring-orange-500 bg-gray-50"
                  placeholder="Enter detailed information..."
                />
              );
            case "dropdown":
              return (
                <select
                  value={secondaryValue || ""}
                  onChange={(e) => onSecondaryChange(e.target.value)}
                  className="w-full rounded-lg border-gray-300 p-2 text-sm shadow-sm focus:border-orange-500 focus:ring-orange-500 bg-gray-50"
                >
                  <option value="">Select an option</option>
                  {secondaryOptions.map((opt, idx) => (
                    <option key={idx} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              );
            default:
              return null;
          }
        })()}
      </div>
    );
  };

  return (
    <div className="flex flex-col space-y-1">
      {renderPrimaryInput()}
      {renderSecondaryInput()}
    </div>
  );
};

const RemarksBubble = ({ label, text }) => (
  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
    <span className="font-semibold">{label}:</span> {text}
  </div>
);

// FILTER HELPERS

const ChecklistFilters = ({
  filters,
  setFilters,
  contractorOptions,
  typeOptions,
  wingOptions = ["A", "B", "C", "D", "E", "F", "G", "NTA"],
  hazardOptions = [
    "1. Physical Hazard",
    "2. Biological Hazard",
    "3. Chemical Hazard",
    "4. Mechanical Hazard",
    "5. Ergonomical Hazard",
    "6. Environmental Hazard",
    "7. Fire/Explosion Hazard",
    "8. Electrical Hazard",
    "9. Psychological Hazard",
    "10. Other Hazards",
  ],
  checkerOptions = [],
  checkerLoading = false,
}) => {
  const isObservations = window.location.pathname.includes(
    "/safety/observations",
  );

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      contractor: "all",
      status: "all",
      type: "all",
      wing: "all",
      hazard: "all",
      risk: "all",
      checker: "all",
    });
  };

  return (
    <div className="rounded-2xl border border-slate-200/90 bg-card p-4 sm:p-5 shadow-2xs">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-foreground">Filters</h3>

        <button
          type="button"
          onClick={clearFilters}
          className="rounded-lg border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
        >
          Clear
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            From Date
          </label>
          <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => updateFilter("fromDate", e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            To Date
          </label>
          <input
            type="date"
            value={filters.toDate}
            onChange={(e) => updateFilter("toDate", e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Contractor
          </label>
          <select
            value={filters.contractor}
            onChange={(e) => updateFilter("contractor", e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          >
            <option value="all">All Contractors</option>
            {contractorOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Status
          </label>
          <select
            value={filters.status}
            onChange={(e) => updateFilter("status", e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          >
            <option value="all">All</option>
            {isObservations ? (
              <>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
                <option value="rectification">Rectification</option>
                <option value="re rectification">Re Rectification</option>
              </>
            ) : (
              <>
                <option value="rejected">Rejected</option>
                <option value="approved">Approved</option>
              </>
            )}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Type
          </label>
          <select
            value={filters.type}
            onChange={(e) => updateFilter("type", e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          >
            <option value="all">All Types</option>
            {isObservations ? (
              <>
                <option value="Unsafe Act">Unsafe Act</option>
                <option value="Unsafe Condition">Unsafe Condition</option>
              </>
            ) : (
              typeOptions.map((title) => (
                <option key={title} value={title}>
                  {title}
                </option>
              ))
            )}
          </select>
        </div>

        {isObservations && (
          <>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Wing
              </label>
              <select
                value={filters.wing}
                onChange={(e) => updateFilter("wing", e.target.value)}
                className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              >
                <option value="all">All Wings</option>
                {wingOptions?.map((wing) => (
                  <option key={wing} value={wing}>
                    {wing}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Hazard
              </label>
              <select
                value={filters.hazard}
                onChange={(e) => updateFilter("hazard", e.target.value)}
                className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              >
                <option value="all">All Hazards</option>
                {hazardOptions?.map((hazard) => (
                  <option key={hazard} value={hazard}>
                    {hazard}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Risk
          </label>
          <select
            value={filters.risk}
            onChange={(e) => updateFilter("risk", e.target.value)}
            className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          >
            <option value="all">All Risks</option>
            <option value="Low Risk">Low Risk</option>
            <option value="Medium Risk">Medium Risk</option>
            <option value="High Risk">High Risk</option>
          </select>
        </div>

        {isObservations && (
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Checker
            </label>
            <select
              value={filters.checker || "all"}
              onChange={(e) => updateFilter("checker", e.target.value)}
              disabled={checkerLoading}
              className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 disabled:opacity-50"
            >
              <option value="all">All Checkers</option>
              {checkerOptions?.map((c) => (
                <option key={c.id ?? c.value} value={c.id ?? c.value}>
                  {c.name || c.label || c.display_name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

const normalizeFilterValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getChecklistCreatedDate = (item) =>
  item?.created_at ? String(item.created_at).slice(0, 10) : "";

const getChecklistContractor = (item) =>
  String(
    item?.contractor_name ||
    item?.name_of_contractor ||
    item?.safety_report_meta?.name_of_contractor ||
    "",
  ).trim();

const getChecklistType = (item) =>
  String(item?.template_title || item?.name || "").trim();

const isApprovedChecklist = (item) =>
  String(item?.status || "").toLowerCase() === "completed" ||
  String(item?.review_state?.closed_reason || "").toLowerCase() === "approved";

const isRejectedChecklist = (item) =>
  String(item?.status || "").toLowerCase() === "closed_rejected" ||
  Number(item?.maker_rejected_points || 0) > 0;

const applyChecklistFilters = (list, filters) => {
  const isObservations = window.location.pathname.includes(
    "/safety/observations",
  );
  return (list || []).filter((item) => {
    const createdDate = getChecklistCreatedDate(item);

    if (filters.fromDate && (!createdDate || createdDate < filters.fromDate)) {
      return false;
    }

    if (filters.toDate && (!createdDate || createdDate > filters.toDate)) {
      return false;
    }

    if (
      filters.contractor &&
      filters.contractor !== "all" &&
      normalizeFilterValue(getChecklistContractor(item)) !==
      normalizeFilterValue(filters.contractor)
    ) {
      return false;
    }

    if (isObservations) {
      if (
        filters.type &&
        filters.type !== "all" &&
        item.unsafe_act_condition_category !== filters.type
      ) {
        return false;
      }
      if (filters.wing && filters.wing !== "all") {
        const w = filters.wing.toLowerCase();
        const locWing = String(item.location_wing || "").toLowerCase();
        const locComb = String(
          item.location_combined || item.location || "",
        ).toLowerCase();

        const hasWing =
          locWing === w ||
          locWing === `wing ${w}` ||
          locComb.includes(`wing ${w}`) ||
          locComb.startsWith(`${w},`) ||
          locComb === w ||
          locComb.includes(` ${w},`);

        if (!hasWing) {
          return false;
        }
      }
      if (filters.hazard && filters.hazard !== "all") {
        let hazardsArray = [];
        try {
          if (item.hazard_categories) {
            hazardsArray =
              typeof item.hazard_categories === "string"
                ? JSON.parse(item.hazard_categories)
                : item.hazard_categories;
            if (!Array.isArray(hazardsArray))
              hazardsArray = [String(item.hazard_categories)];
          }
        } catch (e) {
          hazardsArray = [String(item.hazard_categories)];
        }
        if (!hazardsArray || hazardsArray.length === 0) return false;
        const hasHazard = hazardsArray.some(
          (h) => h === filters.hazard || h.startsWith(filters.hazard + ":"),
        );
        if (!hasHazard) return false;
      }

      if (filters.risk && filters.risk !== "all") {
        if (item.risk !== filters.risk) return false;
      }

      if (filters.checker && filters.checker !== "all") {
        const selectedCheckerId = String(filters.checker).trim();
        const directCheckerId = String(item.checker_id || "").trim();
        const directCheckerName = String(item.checker_name || "").trim().toLowerCase();
        const creatorCheckerId =
          ["CHECKER", "CONTRACTOR_CHECKER"].includes(
            String(item.creator_type || "").toUpperCase(),
          )
            ? String(item.creator_id || "").trim()
            : "";
        const creatorCheckerName =
          ["CHECKER", "CONTRACTOR_CHECKER"].includes(
            String(item.creator_type || "").toUpperCase(),
          )
            ? String(item.creator_name || "").trim().toLowerCase()
            : "";

        const attemptCheckerMatch =
          Array.isArray(item.attempts) &&
          item.attempts.some((att) => {
            if (
              String(att?.checker_id || "").trim() === selectedCheckerId ||
              String(att?.checker_name || "").trim().toLowerCase() === selectedCheckerId.toLowerCase()
            ) {
              return true;
            }
            if (Array.isArray(att?.assignments)) {
              return att.assignments.some(
                (assign) =>
                  (String(assign?.safety_officer_id || "").trim() === selectedCheckerId ||
                   String(assign?.safety_officer_name || "").trim().toLowerCase() === selectedCheckerId.toLowerCase()) &&
                  ["CHECKER", "CONTRACTOR_CHECKER"].includes(
                    String(assign?.assignment_role || "").toUpperCase(),
                  ),
              );
            }
            return false;
          });

        const matchesChecker =
          directCheckerId === selectedCheckerId ||
          creatorCheckerId === selectedCheckerId ||
          directCheckerName === selectedCheckerId.toLowerCase() ||
          creatorCheckerName === selectedCheckerId.toLowerCase() ||
          attemptCheckerMatch;

        if (!matchesChecker) {
          return false;
        }
      }

      if (filters.status && filters.status !== "all") {
        const status = String(item.status || "").toLowerCase();
        if (filters.status === "open") {
          if (
            status === "approved" ||
            status === "rejected" ||
            status === "closed_rejected" ||
            status === "completed"
          )
            return false;
        } else if (filters.status === "closed") {
          if (
            status !== "approved" &&
            status !== "rejected" &&
            status !== "closed_rejected" &&
            status !== "completed"
          )
            return false;
        } else if (filters.status === "rectification") {
          if (
            !["pending_maker", "pending_contractor_maker"].includes(status)
          ) {
            return false;
          }
        } else if (filters.status === "re rectification") {
          if (
            !["pending_checker", "pending_contractor_checker"].includes(status)
          ) {
            return false;
          }
        }
      }
    } else {
      if (
        filters.type &&
        filters.type !== "all" &&
        normalizeFilterValue(getChecklistType(item)) !==
        normalizeFilterValue(filters.type)
      ) {
        return false;
      }

      if (filters.status === "approved" && !isApprovedChecklist(item)) {
        return false;
      }

      if (filters.status === "rejected" && !isRejectedChecklist(item)) {
        return false;
      }
    }

    return true;
  });
};

// const ReportHeaderInfoCard = ({ detail }) => {
//     const meta =
//         detail?.report_header_meta ||
//         detail?.safety_report_meta ||
//         detail?.report_meta ||
//         {};

//     const rows = [
//         ["Format No.", meta.format_no],
//         ["Revision No.", meta.revision_no],
//         ["Issued Date", meta.issued_date],
//         ["Revision Date", meta.revision_date],
//         ["Project", meta.project],
//         ["Inspection Report No.", meta.inspection_report_no],
//         ["Name of Contractor", meta.name_of_contractor],
//         ["Date of Inspection", meta.date_of_inspection],
//         ["Make / Model", meta.make_model],
//         ["Identification No.", meta.identification_no],
//         ["Location", meta.location],
//         ["Name of Operator", meta.name_of_operator],
//     ];

//     return (
//         <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm">
//             <h2 className="mb-4 text-sm font-bold text-foreground">
//                 Checklist Details
//             </h2>

//             <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
//                 {rows.map(([label, value]) => (
//                     <div
//                         key={label}
//                         className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2"
//                     >
//                         <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
//                             {label}
//                         </p>
//                         <p className="mt-1 break-words text-sm font-semibold text-foreground">
//                             {String(value || "").trim() || "—"}
//                         </p>
//                     </div>
//                 ))}
//             </div>
//         </div>
//     );
// };

// const formatTodayDDMMYYYY = () => {
//     const now = new Date();
//     const dd = String(now.getDate()).padStart(2, "0");
//     const mm = String(now.getMonth() + 1).padStart(2, "0");
//     const yyyy = now.getFullYear();

//     return `${dd}-${mm}-${yyyy}`;
// };

// const ReadOnlyHeaderField = ({ label, value }) => (
//     <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
//         <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
//             {label}
//         </p>
//         <p className="mt-1 break-words text-sm font-semibold text-foreground">
//             {String(value || "").trim() || "—"}
//         </p>
//     </div>
// );

// const EditableHeaderInput = ({ label, value, onChange, placeholder }) => (
//     <div>
//         <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
//             {label}
//         </label>
//         <input
//             type="text"
//             value={value || ""}
//             onChange={(e) => onChange(e.target.value)}
//             className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
//             placeholder={placeholder}
//         />
//     </div>
// );

// const ReportHeaderCreateCard = ({
//     selectedTemplate,
//     reportMeta,
//     setReportMeta,
//     projectId,
//     currentUserProfile,
//     isInternalMaker,
//     contractors,
//     contractorsLoading,
// }) => {
//     const templateMeta = selectedTemplate?.report_header_meta || {};

//     const reportPrefix = String(
//         templateMeta.inspection_report_prefix ||
//         templateMeta.inspection_report_no ||
//         ""
//     )
//         .trim()
//         .replace(/-$/, "");

//     const externalContractorName =
//         currentUserProfile?.contractor_name ||
//         templateMeta.name_of_contractor ||
//         "";

//     return (
//         <div className="mb-4 rounded-xl border bg-card p-5 shadow-sm">
//             <div className="mb-4">
//                 <h3 className="text-sm font-semibold text-foreground">
//                     Inspection Details
//                 </h3>
//                 {/* <p className="mt-1 text-xs text-muted-foreground">
//                     Report header details. Only input fields are editable.
//                 </p> */}
//             </div>

//             <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
//                 <ReadOnlyHeaderField
//                     label="Format No."
//                     value={templateMeta.format_no}
//                 />

//                 <ReadOnlyHeaderField
//                     label="Revision No."
//                     value={templateMeta.revision_no || "R01"}
//                 />

//                 <ReadOnlyHeaderField
//                     label="Issued Date"
//                     value={templateMeta.issued_date}
//                 />

//                 <ReadOnlyHeaderField
//                     label="Revision Date"
//                     value={templateMeta.revision_date}
//                 />

//                 <ReadOnlyHeaderField
//                     label="Project"
//                     value={templateMeta.project || (projectId ? `Project ${projectId}` : "")}
//                 />

//                 <ReadOnlyHeaderField
//                     label="Inspection Report No."
//                     value={
//                         reportPrefix
//                             ? `${reportPrefix}-Auto`
//                             : "Auto-generated on submit"
//                     }
//                 />

//                 <ReadOnlyHeaderField
//                     label="Date of Inspection"
//                     value={formatTodayDDMMYYYY()}
//                 />

//                 {isInternalMaker ? (
//                     <div>
//                         <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
//                             Name of Contractor
//                         </label>

//                         <select
//                             value={reportMeta.name_of_contractor}
//                             onChange={(e) =>
//                                 setReportMeta((prev) => ({
//                                     ...prev,
//                                     name_of_contractor: e.target.value,
//                                 }))
//                             }
//                             disabled={contractorsLoading}
//                             className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
//                         >
//                             <option value="">
//                                 {contractorsLoading
//                                     ? "Loading contractors..."
//                                     : "Select Contractor"}
//                             </option>

//                             {contractors.map((contractor) => {
//                                 const name =
//                                     typeof contractor === "string"
//                                         ? contractor
//                                         : contractor?.name || contractor?.contractor_name || "";

//                                 if (!name) return null;

//                                 return (
//                                     <option key={name} value={name}>
//                                         {name}
//                                     </option>
//                                 );
//                             })}
//                         </select>

//                         {!contractorsLoading && contractors.length === 0 && (
//                             <p className="mt-1 text-xs text-red-500">
//                                 No contractor found in this project.
//                             </p>
//                         )}
//                     </div>
//                 ) : (
//                     <ReadOnlyHeaderField
//                         label="Name of Contractor"
//                         value={externalContractorName}
//                     />
//                 )}

//                 <EditableHeaderInput
//                     label="Make / Model"
//                     value={reportMeta.make_model}
//                     placeholder="Enter make / model"
//                     onChange={(value) =>
//                         setReportMeta((prev) => ({
//                             ...prev,
//                             make_model: value,
//                         }))
//                     }
//                 />

//                 <EditableHeaderInput
//                     label="Identification No."
//                     value={reportMeta.identification_no}
//                     placeholder="Enter identification no."
//                     onChange={(value) =>
//                         setReportMeta((prev) => ({
//                             ...prev,
//                             identification_no: value,
//                         }))
//                     }
//                 />

//                 <EditableHeaderInput
//                     label="Location"
//                     value={reportMeta.location}
//                     placeholder="Enter location"
//                     onChange={(value) =>
//                         setReportMeta((prev) => ({
//                             ...prev,
//                             location: value,
//                         }))
//                     }
//                 />

//                 <EditableHeaderInput
//                     label="Name of Operator"
//                     value={reportMeta.name_of_operator}
//                     placeholder="Enter operator name"
//                     onChange={(value) =>
//                         setReportMeta((prev) => ({
//                             ...prev,
//                             name_of_operator: value,
//                         }))
//                     }
//                 />
//             </div>

//             {isInternalMaker && (
//                 <p className="mt-3 text-xs text-gray-500">
//                     Select the Contractor Name accordingly.
//                 </p>
//             )}
//         </div>
//     );
// };

const LEGACY_HEADER_FIELDS = [
  {
    key: "format_no",
    label: "Format No.",
    source: "TEMPLATE_FIXED",
    input_type: "text",
    system_key: "",
    editable_by_maker: false,
    row_index: 1,
    column_index: 1,
    order_index: 1,
  },
  {
    key: "revision_no",
    label: "Revision No.",
    source: "TEMPLATE_FIXED",
    input_type: "text",
    system_key: "",
    editable_by_maker: false,
    row_index: 1,
    column_index: 2,
    order_index: 2,
  },
  {
    key: "issued_date",
    label: "Issued Date",
    source: "TEMPLATE_FIXED",
    input_type: "date",
    system_key: "",
    editable_by_maker: false,
    row_index: 2,
    column_index: 1,
    order_index: 3,
  },
  {
    key: "revision_date",
    label: "Revision Date",
    source: "TEMPLATE_FIXED",
    input_type: "date",
    system_key: "",
    editable_by_maker: false,
    row_index: 2,
    column_index: 2,
    order_index: 4,
  },
  {
    key: "project",
    label: "Project",
    source: "PROJECT_CONTEXT",
    input_type: "text",
    system_key: "project_name",
    editable_by_maker: false,
    row_index: 3,
    column_index: 1,
    order_index: 5,
  },
  {
    key: "inspection_report_no",
    label: "Inspection Report No.",
    source: "SYSTEM_GENERATED",
    input_type: "text",
    system_key: "report_no",
    editable_by_maker: false,
    row_index: 3,
    column_index: 2,
    order_index: 6,
  },
  {
    key: "name_of_contractor",
    label: "Name of Contractor",
    source: "MAKER_INPUT",
    input_type: "text",
    system_key: "",
    editable_by_maker: true,
    row_index: 4,
    column_index: 1,
    order_index: 7,
  },
  {
    key: "date_of_inspection",
    label: "Date of Inspection",
    source: "SYSTEM_GENERATED",
    input_type: "date",
    system_key: "inspection_date",
    editable_by_maker: false,
    row_index: 4,
    column_index: 2,
    order_index: 8,
  },
  {
    key: "make_model",
    label: "Make / Model",
    source: "MAKER_INPUT",
    input_type: "text",
    system_key: "",
    editable_by_maker: true,
    row_index: 5,
    column_index: 1,
    order_index: 9,
  },
  {
    key: "identification_no",
    label: "Identification No.",
    source: "MAKER_INPUT",
    input_type: "text",
    system_key: "",
    editable_by_maker: true,
    row_index: 5,
    column_index: 2,
    order_index: 10,
  },
  {
    key: "location",
    label: "Location",
    source: "MAKER_INPUT",
    input_type: "text",
    system_key: "",
    editable_by_maker: true,
    row_index: 6,
    column_index: 1,
    order_index: 11,
  },
  {
    key: "name_of_operator",
    label: "Name of Operator",
    source: "MAKER_INPUT",
    input_type: "text",
    system_key: "",
    editable_by_maker: true,
    row_index: 6,
    column_index: 2,
    order_index: 12,
  },
];

const formatTodayDDMMYYYY = () => {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const yyyy = now.getFullYear();

  return `${dd}-${mm}-${yyyy}`;
};

const sortHeaderFields = (fields = []) =>
  [...fields].sort(
    (a, b) =>
      Number(a.row_index || 0) - Number(b.row_index || 0) ||
      Number(a.column_index || 0) - Number(b.column_index || 0) ||
      Number(a.order_index || 0) - Number(b.order_index || 0),
  );

const buildLegacyHeaderFields = (meta = {}) =>
  LEGACY_HEADER_FIELDS.map((field) => ({
    ...field,
    default_value: String(meta?.[field.key] || "").trim(),
    required: false,
    visible_in_report: true,
    visible_in_preview: true,
    validation_config: {},
  }));

const getTemplateHeaderFields = (template) => {
  const configuredFields = Array.isArray(template?.header_fields)
    ? template.header_fields
    : [];

  if (configuredFields.length > 0) {
    return sortHeaderFields(configuredFields);
  }

  return sortHeaderFields(
    buildLegacyHeaderFields(template?.report_header_meta || {}),
  );
};

const getChecklistResolvedHeaderFields = (detail) => {
  const configuredFields = Array.isArray(detail?.report_header_fields)
    ? detail.report_header_fields
    : [];

  if (configuredFields.length > 0) {
    return sortHeaderFields(configuredFields);
  }

  const meta =
    detail?.report_header_meta ||
    detail?.safety_report_meta ||
    detail?.report_meta ||
    {};

  return sortHeaderFields(
    buildLegacyHeaderFields(meta).map((field) => ({
      ...field,
      value: String(meta?.[field.key] || "").trim(),
    })),
  );
};

const isEditableMakerField = (field) =>
  field?.source === "MAKER_INPUT" && field?.editable_by_maker === true;

const isRequiredMakerField = (field) => {
  if (!isEditableMakerField(field)) {
    return false;
  }

  /*
        Business rule:
        Any Maker-input field shown on the checklist creation form is mandatory
        unless the setup explicitly marks it optional through validation_config.optional.
    */
  return field?.validation_config?.optional !== true;
};

const getExternalContractorName = (currentUserProfile = {}) =>
  String(
    currentUserProfile?.contractor_name ||
    currentUserProfile?.contractor?.name ||
    "",
  ).trim();

const buildInitialReportMeta = (template, currentUserProfile = {}) => {
  const nextMeta = {};
  const externalContractorName = getExternalContractorName(currentUserProfile);

  getTemplateHeaderFields(template)
    .filter(isEditableMakerField)
    .forEach((field) => {
      let initialValue = String(field.default_value || "").trim();

      if (field.key === "name_of_contractor" && externalContractorName) {
        initialValue = externalContractorName;
      }

      nextMeta[field.key] = initialValue;
    });

  return nextMeta;
};

const resolveTemplateFieldDisplayValue = ({
  field,
  selectedTemplate,
  projectId,
  projectConfigs,
}) => {
  const defaultValue = String(field?.default_value || "").trim();

  if (field.source === "TEMPLATE_FIXED") {
    return defaultValue;
  }

  if (field.source === "PROJECT_CONTEXT") {
    if (field.system_key === "project_name") {
      return (
        defaultValue ||
        selectedTemplate?.project_name ||
        (projectId ? `Project ${projectId}` : "")
      );
    }

    return defaultValue;
  }

  if (field.source === "SYSTEM_GENERATED") {
    if (field.system_key === "report_no") {
      return getReportNumberPreview(selectedTemplate, projectId, projectConfigs);
    }

    if (
      field.system_key === "inspection_date" ||
      field.system_key === "created_date"
    ) {
      return formatTodayDDMMYYYY();
    }

    return "Auto-generated";
  }

  return defaultValue;
};

const ReadOnlyHeaderField = ({ label, value }) => (
  <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
    <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {label}
    </p>

    <p className="mt-1 break-words text-sm font-semibold text-foreground">
      {String(value || "").trim() || "—"}
    </p>
  </div>
);

const EditableHeaderInput = ({ field, value, onChange }) => {
  const options = Array.isArray(field?.validation_config?.options)
    ? field.validation_config.options
    : [];

  if (field.input_type === "select") {
    return (
      <div>
        <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {field.label}
          {isRequiredMakerField(field) ? (
            <span className="ml-0.5 text-red-500">*</span>
          ) : null}
        </label>

        <select
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
        >
          <option value="">Select {field.label}</option>

          {options.map((option) => {
            const optionValue =
              typeof option === "string"
                ? option
                : option?.value || option?.label || "";

            const optionLabel =
              typeof option === "string"
                ? option
                : option?.label || option?.value || "";

            if (!optionValue) return null;

            return (
              <option key={optionValue} value={optionValue}>
                {optionLabel}
              </option>
            );
          })}
        </select>
      </div>
    );
  }

  const inputType = ["date", "number"].includes(field.input_type)
    ? field.input_type
    : "text";

  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {field.label}
        {isRequiredMakerField(field) ? (
          <span className="ml-0.5 text-red-500">*</span>
        ) : null}
      </label>

      <input
        type={inputType}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
        placeholder={`Enter ${String(field.label || "").toLowerCase()}`}
      />
    </div>
  );
};

const ReportHeaderInfoCard = ({ detail }) => {
  const resolvedFields = getChecklistResolvedHeaderFields(detail).filter(
    (field) => field.visible_in_preview !== false,
  );

  return (
    <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm">
      <h2 className="mb-4 text-sm font-bold text-foreground">
        Checklist Details
      </h2>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {resolvedFields.map((field) => (
          <ReadOnlyHeaderField
            key={field.key}
            label={field.label}
            value={field.value ?? field.default_value ?? ""}
          />
        ))}
      </div>
    </div>
  );
};

export const ReportHeaderCreateCard = ({
  selectedTemplate,
  reportMeta,
  setReportMeta,
  projectId,
  projectConfigs,
  currentUserProfile,
  isInternalMaker,
  contractors,
  contractorsLoading,
}) => {
  const configuredFields = getTemplateHeaderFields(selectedTemplate);
  const externalContractorName = getExternalContractorName(currentUserProfile);

  return (
    <div className="mb-4 rounded-xl border bg-card p-5 shadow-sm">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-foreground">
          Inspection Details
        </h3>

        <p className="mt-1 text-xs text-muted-foreground">
          Complete the inspection details below before answering the checklist
          questions. Required fields marked with * must be filled and will be
          included in the generated report.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {configuredFields.map((field) => {
          const editableByMaker = isEditableMakerField(field);

          if (!editableByMaker) {
            return (
              <ReadOnlyHeaderField
                key={field.key}
                label={field.label}
                value={resolveTemplateFieldDisplayValue({
                  field,
                  selectedTemplate,
                  projectId,
                  projectConfigs,
                })}
              />
            );
          }

          if (field.key === "name_of_contractor" && isInternalMaker) {
            return (
              <div key={field.key}>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {field.label}
                  {field.required ? " *" : ""}
                </label>

                <select
                  value={reportMeta[field.key] || ""}
                  onChange={(e) =>
                    setReportMeta((prev) => ({
                      ...prev,
                      [field.key]: e.target.value,
                    }))
                  }
                  disabled={contractorsLoading}
                  className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                >
                  <option value="">
                    {contractorsLoading
                      ? "Loading contractors..."
                      : "Select Contractor"}
                  </option>

                  {contractors.map((contractor) => {
                    const name =
                      typeof contractor === "string"
                        ? contractor
                        : contractor?.name || contractor?.contractor_name || "";

                    if (!name) return null;

                    return (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    );
                  })}
                </select>

                {!contractorsLoading && contractors.length === 0 && (
                  <p className="mt-1 text-xs text-red-500">
                    No contractor found in this project.
                  </p>
                )}
              </div>
            );
          }

          if (field.key === "name_of_contractor" && !isInternalMaker) {
            return (
              <ReadOnlyHeaderField
                key={field.key}
                label={field.label}
                value={
                  reportMeta[field.key] ||
                  externalContractorName ||
                  field.default_value ||
                  ""
                }
              />
            );
          }

          return (
            <EditableHeaderInput
              key={field.key}
              field={field}
              value={reportMeta[field.key] || ""}
              onChange={(value) =>
                setReportMeta((prev) => ({
                  ...prev,
                  [field.key]: value,
                }))
              }
            />
          );
        })}
      </div>

      {isInternalMaker &&
        configuredFields.some(
          (field) =>
            field.key === "name_of_contractor" && isEditableMakerField(field),
        ) && (
          <p className="mt-3 text-xs text-gray-500">
            Select the Contractor Name accordingly.
          </p>
        )}
    </div>
  );
};

// ─────────────────────────────────────────────
// MakerDashboard
// ─────────────────────────────────────────────
export default function SafetyChecklistCreateView({
  onBack,
  allowedFirstRoles = ["MAKER", "INITIALIZER"],
}) {
  const userId = getCurrentUserId();

  const [projectId, setProjectId] = useState("");
  const [orgId, setOrgId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [createAnswers, setCreateAnswers] = useState({});
  const [createRemarks, setCreateRemarks] = useState({});
  const [createMedia, setCreateMedia] = useState({});
  const [reportMeta, setReportMeta] = useState({});

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    contractor: "all",
    status: "all",
    type: "all",
    wing: "all",
    hazard: "all",
    risk: "all",
    checker: "all",
  });

  const [checkerOptions, setCheckerOptions] = useState([]);
  const [checkerLoading, setCheckerLoading] = useState(false);

  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState(() =>
    window.location.pathname.includes("/safety/observations")
      ? "dashboard"
      : "create",
  ); // "create" | "dashboard" | "maker_fix"
  const [makerRemarks, setMakerRemarks] = useState({});
  const [makerMedia, setMakerMedia] = useState({});
  const [makerAnswers, setMakerAnswers] = useState({});
  const [isMakerSignatureModalOpen, setIsMakerSignatureModalOpen] =
    useState(false);
  const [pendingSubmitPayload, setPendingSubmitPayload] = useState(null);
  const [signatureSubmitting, setSignatureSubmitting] = useState(false);

  // Safety Observation Maker state.
  const [observationComment, setObservationComment] = useState("");
  const [observationPhotos, setObservationPhotos] = useState([]);
  const [observationSubmissionRoute, setObservationSubmissionRoute] =
    useState("");
  const [showRiskMatrix, setShowRiskMatrix] = useState(false);

  const [currentUserProfile, setCurrentUserProfile] = useState({});
  const [contractors, setContractors] = useState([]);
  const [contractorsLoading, setContractorsLoading] = useState(false);
  const [projectConfigs, setProjectConfigs] = useState(() => {
    try {
      const cached = JSON.parse(
        localStorage.getItem("SAFETY_PROJECT_REPORT_CONFIGS") || "{}",
      );
      const map = {};
      Object.entries(cached).forEach(([pid, code]) => {
        map[pid] = { project_id: Number(pid), project_code: code };
      });
      return map;
    } catch {
      return {};
    }
  });

  const [signaturePurpose, setSignaturePurpose] = useState(null);
  const [pendingCreateSubmitFormData, setPendingCreateSubmitFormData] =
    useState(null);

  const [historyChecklist, setHistoryChecklist] = useState(null);

  const [readonlyChecklist, setReadonlyChecklist] = useState(null);
  const [readonlyPendingStatusLabel, setReadonlyPendingStatusLabel] =
    useState("Pending for Review");

  const currentUserType = String(
    currentUserProfile?.user_type || "",
  ).toUpperCase();
  const isInternalMaker = currentUserType === "INTERNAL";
  const isExternalMaker = currentUserType === "EXTERNAL";
  const isObservations = window.location.pathname.includes(
    "/safety/observations",
  );

  const observationCreatorType = String(detail?.creator_type || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  const observationContractorName = String(
    detail?.contractor_name || detail?.name_of_contractor || "",
  ).trim();

  const observationHasContractor = Boolean(observationContractorName);
  const observationCreatedByContractorChecker =
    observationCreatorType === "CONTRACTOR_CHECKER";

  const autoAssignedObservationRoute =
    isExternalMaker &&
      observationHasContractor &&
      observationCreatedByContractorChecker
      ? "CONTRACTOR_CHECKER"
      : "CHECKER";

  useEffect(() => {
    if (detail && isObservations) {
      setObservationSubmissionRoute(autoAssignedObservationRoute);
    }
  }, [detail, isObservations, autoAssignedObservationRoute]);

  // ── Safety Observation attempt / rejection history ─────────────
  const observationAttempts = Array.isArray(detail?.attempts)
    ? [...detail.attempts].sort(
      (a, b) => Number(a?.attempt_no || 0) - Number(b?.attempt_no || 0),
    )
    : [];

  const observationCurrentAttemptNo = Number(detail?.current_attempt_no || 1);

  const observationPreviousAttempts = observationAttempts.filter(
    (attempt) => Number(attempt?.attempt_no || 0) < observationCurrentAttemptNo,
  );

  const observationRejectionHistory = (() => {
    try {
      if (Array.isArray(detail?.rejection_history)) {
        return detail.rejection_history;
      }

      const parsed = JSON.parse(detail?.rejection_history || "[]");

      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  })();

  const getObservationPhotoPath = (photo) =>
    photo?.annotated_image_url ||
    photo?.annotated_image ||
    photo?.image_url ||
    photo?.image ||
    null;

  const formatObservationWorkflowDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getObservationAttemptRejections = (attemptNo) =>
    observationRejectionHistory.filter(
      (entry) => Number(entry?.attempt_no || 0) === Number(attemptNo || 0),
    );

  const renderObservationHistoryPhotos = (
    photos,
    { emptyText = "No photograph attached.", isCheckerPhoto = false } = {},
  ) => {
    const list = Array.isArray(photos) ? photos : [];

    if (!list.length) {
      return (
        <p className="text-sm italic text-muted-foreground">{emptyText}</p>
      );
    }

    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        {list.map((photo, index) => {
          const photoPath = getObservationPhotoPath(photo);

          return (
            <div
              key={photo?.id || index}
              className={`rounded-lg border p-3 ${isCheckerPhoto
                  ? "border-red-200 bg-red-50/40"
                  : "border-orange-200 bg-orange-50/40"
                }`}
            >
              {photoPath ? (
                <img
                  src={resolveMediaUrl(photoPath)}
                  alt={
                    isCheckerPhoto
                      ? `Checker rejection ${index + 1}`
                      : `Maker submission ${index + 1}`
                  }
                  className="h-40 w-full rounded-lg border border-border bg-white object-contain"
                />
              ) : (
                <div className="flex h-28 items-center justify-center rounded-lg border border-dashed border-border bg-white text-xs text-muted-foreground">
                  Image unavailable
                </div>
              )}

              {photo?.comment && (
                <p className="mt-2 whitespace-pre-wrap text-xs text-foreground">
                  <span className="font-semibold">Remark:</span> {photo.comment}
                </p>
              )}

              {photo?.checker_comment && (
                <p className="mt-2 whitespace-pre-wrap rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  <span className="font-semibold">Checker Comment:</span>{" "}
                  {photo.checker_comment}
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderObservationRejectedAttempt = (attempt) => {
    const attemptNo = Number(attempt?.attempt_no || 0);

    const makerPhotos = Array.isArray(attempt?.safety_officer_photographs)
      ? attempt.safety_officer_photographs
      : [];

    const checkerPhotos = Array.isArray(attempt?.checker_reject_photographs)
      ? attempt.checker_reject_photographs
      : [];

    let attemptRejections = getObservationAttemptRejections(attemptNo);

    if (
      attemptRejections.length === 0 &&
      (checkerPhotos.length > 0 ||
        String(attempt?.checker_decision || "").toUpperCase() === "REJECTED" ||
        (Number(detail?.current_attempt_no || 0) > attemptNo &&
          detail?.status === "ASSIGNED_TO_MAKER"))
    ) {
      attemptRejections = [
        {
          attempt_no: attemptNo,
          checker_name:
            checkerPhotos[0]?.uploaded_by_checker_name ||
            (detail?.checker_name &&
              detail.checker_name !== attempt?.checker_name
              ? detail.checker_name
              : "Safety.Checker1"),
          checker_level: "CHECKER",
          decision: "REJECTED",
          reason:
            checkerPhotos[0]?.comment ||
            detail?.checker_remarks ||
            attempt?.checker_reason ||
            "",
          reviewed_at:
            detail?.checker_reviewed_at ||
            attempt?.checker_reviewed_at ||
            null,
        },
      ];
    }

    const latestRejection =
      attemptRejections[attemptRejections.length - 1] || null;

    const rejectingCheckerName =
      latestRejection?.checker_name ||
      checkerPhotos[0]?.uploaded_by_checker_name ||
      (detail?.checker_name && detail.checker_name !== attempt?.checker_name
        ? detail.checker_name
        : null) ||
      (attempt?.submission_route === "CONTRACTOR_CHECKER"
        ? "Safety.Checker1"
        : attempt?.checker_name) ||
      "Safety.Checker1";

    const checkerName = rejectingCheckerName;

    const checkerLevel = latestRejection?.checker_level
      ? String(latestRejection.checker_level).toUpperCase() === "CHECKER"
        ? "ADL EHS (Checker)"
        : String(latestRejection.checker_level).replace(/_/g, " ")
      : "ADL EHS (Checker)";

    const isContractorCheckerRoute =
      attempt?.submission_route === "CONTRACTOR_CHECKER" ||
      (attempt?.checker_name &&
        String(attempt?.checker_name).toLowerCase().includes("contractor")) ||
      (Boolean(attempt?.checker_name) &&
        Boolean(rejectingCheckerName) &&
        attempt?.checker_name !== rejectingCheckerName);

    const rejectionReason =
      latestRejection?.reason ||
      latestRejection?.remarks ||
      (detail?.checker_name === rejectingCheckerName
        ? detail?.checker_remarks
        : "") ||
      checkerPhotos.find((p) => p?.comment)?.comment ||
      (!isContractorCheckerRoute ? attempt?.checker_reason : "") ||
      "";

    const reviewedAt =
      latestRejection?.reviewed_at ||
      latestRejection?.timestamp ||
      (detail?.checker_name === rejectingCheckerName
        ? detail?.checker_reviewed_at
        : null) ||
      attempt?.checker_reviewed_at ||
      null;

    return (
      <div
        key={attempt?.id || attemptNo}
        className="overflow-hidden rounded-xl border border-red-200 bg-card shadow-sm"
      >
        <div className="border-b border-red-100 bg-red-50 px-4 py-3">
          <p className="text-sm font-bold text-red-800">
            Attempt {attemptNo}
          </p>
          <p className="mt-0.5 text-xs text-red-600">
            {/* Review your previous submission and Checker feedback before fixing
            it again. */}
          </p>
        </div>

        <div className="space-y-4 p-4">
          <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
            <h4 className="mb-3 text-sm font-bold text-orange-700">
              Previous Response
            </h4>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Submitted At
                </p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {formatObservationWorkflowDate(
                    attempt?.safety_officer_submitted_at,
                  )}
                </p>
              </div>
            </div>

            {/* <div className="mt-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Your Remarks
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                {attempt?.safety_officer_comment ||
                  "No Maker remarks were provided."}
              </p>
            </div> */}

            <div className="mt-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Your Photograph(s)
              </p>

              {renderObservationHistoryPhotos(makerPhotos, {
                emptyText: "No Maker photograph was attached to this attempt.",
              })}
            </div>
          </div>

          {isContractorCheckerRoute && attempt?.checker_name && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 text-xs text-emerald-900">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-bold">Forwarded by Contractor Checker:</span>{" "}
                  {attempt.checker_name}
                  {attempt?.checker_reviewed_at && (
                    <span className="ml-2 text-muted-foreground">
                      ({formatObservationWorkflowDate(attempt.checker_reviewed_at)})
                    </span>
                  )}
                </div>
                <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 font-semibold text-emerald-700">
                  FORWARDED
                </span>
              </div>
              {attempt?.checker_reason && (
                <p className="mt-1 text-slate-700">
                  <span className="font-semibold">Note:</span>{" "}
                  {attempt.checker_reason}
                </p>
              )}
            </div>
          )}

          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <h4 className="mb-3 text-sm font-bold text-red-700">
              Reason For Rejection
            </h4>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Rejected By
                </p>
                <p className="mt-1 text-sm font-bold text-foreground">
                  {checkerName}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {checkerLevel}
                </p>
              </div>

              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Rejected At
                </p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {formatObservationWorkflowDate(reviewedAt)}
                </p>
              </div>
            </div>

            <div className="mt-3 rounded-lg border border-red-200 bg-white p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-red-600">
                Rejection Reason
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm font-medium text-red-700">
                {rejectionReason || "No rejection reason was provided."}
              </p>
            </div>

            {attemptRejections.map((entry, index) => {
              const reason = entry?.reason || entry?.remarks || "";

              if (!reason || reason === rejectionReason) {
                return null;
              }

              return (
                <div
                  key={`${attemptNo}-rejection-${index}`}
                  className="mt-3 rounded-lg border border-red-200 bg-white p-3"
                >
                  <p className="text-xs font-bold text-red-700">
                    {String(entry?.checker_level || entry?.stage || "CHECKER")
                      .replace(/_/g, " ")
                      .trim()}
                  </p>

                  <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                    {reason}
                  </p>

                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {formatObservationWorkflowDate(
                      entry?.reviewed_at || entry?.timestamp,
                    )}
                  </p>
                </div>
              );
            })}

            <div className="mt-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-red-600">
                Checker Attached Photograph(s)
              </p>

              {renderObservationHistoryPhotos(checkerPhotos, {
                emptyText: "The Checker did not attach a rejection photograph.",
                isCheckerPhoto: true,
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const sigCanvasRef = useRef(null);
  // Reusable component handles the resize logic now

  // ── data fetching ───────────────────────────────────────────
  const fetchList = async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const params = {
        assigned_to_me: true,
      };

      if (projectId) {
        params.project_id = projectId;
      }

      let res;

      if (isObservations) {
        const orgId = resolveOrgId?.() || null;
        if (orgId) params.org_id = orgId;
        params.paginate = "false";
        res = await fetchAllSafetyObservations(params);
      } else {
        res = await listSafetyChecklists({
          ...params,
          template_type: "SAFETY",
        });
      }

      const data = res?.data;
      const rawList = Array.isArray(data) ? data : (data?.results ?? []);

      const list = isObservations
        ? [...rawList]
            .sort((a, b) => {
              const timeA = new Date(a.created_at || 0).getTime();
              const timeB = new Date(b.created_at || 0).getTime();
              if (timeB !== timeA) return timeB - timeA;
              return Number(b.id || 0) - Number(a.id || 0);
            })
            .map((item) => ({
              ...item,
              name: `Observation #${item?.observation_no ?? item?.id}`,
            }))
        : rawList;

      setChecklists(list);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        (isObservations
          ? "Failed to load Safety Observations."
          : "Failed to load checklists.");

      setError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isObservations) return;
    const effectiveProjectId = projectId || resolveActiveProjectId?.() || "";
    if (!effectiveProjectId) return;

    let isMounted = true;
    const loadCheckers = async () => {
      setCheckerLoading(true);
      try {
        const res = await getSafetyObservationCheckers({ project_id: effectiveProjectId });
        const list = res?.data?.checkers || res?.data?.results || (Array.isArray(res?.data) ? res.data : []);
        if (isMounted) {
          const fromObs = [];
          if (Array.isArray(checklists)) {
            checklists.forEach((obs) => {
              if (obs.checker_id && obs.checker_name) {
                fromObs.push({ id: obs.checker_id, name: obs.checker_name });
              }
              if (
                obs.creator_id &&
                obs.creator_name &&
                ["CHECKER", "CONTRACTOR_CHECKER"].includes(String(obs.creator_type || "").toUpperCase())
              ) {
                fromObs.push({ id: obs.creator_id, name: obs.creator_name });
              }
              if (Array.isArray(obs.attempts)) {
                obs.attempts.forEach((att) => {
                  if (att.checker_id && att.checker_name) {
                    fromObs.push({ id: att.checker_id, name: att.checker_name });
                  }
                  if (Array.isArray(att.assignments)) {
                    att.assignments.forEach((asg) => {
                      if (
                        asg.safety_officer_id &&
                        asg.safety_officer_name &&
                        ["CHECKER", "CONTRACTOR_CHECKER"].includes(String(asg.assignment_role || "").toUpperCase())
                      ) {
                        fromObs.push({ id: asg.safety_officer_id, name: asg.safety_officer_name });
                      }
                    });
                  }
                });
              }
            });
          }

          const map = new Map();
          list.forEach((c) => {
            if (c?.id) map.set(String(c.id), { id: c.id, name: c.name || `Checker #${c.id}` });
          });
          fromObs.forEach((c) => {
            if (c?.id && !map.has(String(c.id))) {
              map.set(String(c.id), { id: c.id, name: c.name || `Checker #${c.id}` });
            }
          });

          const combined = Array.from(map.values()).sort((a, b) =>
            String(a.name).localeCompare(String(b.name))
          );
          setCheckerOptions(combined);
        }
      } catch (err) {
        console.warn("Failed to load checkers dynamically:", err);
      } finally {
        if (isMounted) setCheckerLoading(false);
      }
    };

    loadCheckers();
    return () => {
      isMounted = false;
    };
  }, [isObservations, projectId, checklists]);

  const loadCreateTemplates = async () => {
    let effectiveProjectId = String(projectId || "").trim();
    let effectiveOrgId = String(orgId || "").trim();

    // Important: fresh login case.
    // If state is empty, resolve again from access token / USER_DATA / localStorage.
    if (!effectiveProjectId || !effectiveOrgId) {
      const ctx = resolveMakerContext();

      effectiveProjectId = ctx.projectId;
      effectiveOrgId = ctx.orgId;

      setProjectId(effectiveProjectId);
      setOrgId(effectiveOrgId);
      setCurrentUserProfile(ctx.profile);
    }

    if (!effectiveProjectId || !effectiveOrgId) {
      setTemplates([]);
      showToast(
        "Project or organization not found. Please select/open your project profile once.",
        "error",
      );
      return;
    }

    setTemplatesLoading(true);

    try {
      const isObservations = window.location.pathname.includes(
        "/safety/observations",
      );
      const catRes = await listSafetyCategories({
        org_id: effectiveOrgId,
        project_id: effectiveProjectId,
        active: true,
      });

      const catRaw = catRes?.data;
      const categories = Array.isArray(catRaw)
        ? catRaw
        : (catRaw?.results ?? []);

      const safetyCategory =
        categories.find((c) => {
          const name = String(c.name || "")
            .trim()
            .toLowerCase();
          if (isObservations) {
            return (
              name.includes("observation") || name.includes("housekeeping")
            );
          }
          return name === "safety checklist";
        }) || categories[0];

      if (!safetyCategory) {
        setTemplates([]);
        showToast("Safety Checklist category not found.", "error");
        return;
      }

      const tplRes = await listSafetyTemplates({
        org_id: effectiveOrgId,
      });

      const tplRaw = tplRes?.data;
      const list = Array.isArray(tplRaw) ? tplRaw : (tplRaw?.results ?? []);

      // Filter on frontend to avoid backend filtering bugs
      const activeTemplates = list.filter((t) => {
        const s = String(t.status || "").toUpperCase();
        if (s !== "ACTIVE") return false;

        // Filter by category type based on path
        const catName = String(
          t.category?.name || t.category_name || "",
        ).toLowerCase();
        const isObsTemplate =
          catName.includes("observation") || catName.includes("housekeeping");

        if (isObservations && !isObsTemplate) return false;
        if (!isObservations && isObsTemplate) return false;

        // Restrict based on first workflow step
        const sortedWorkflowConfig =
          t.workflow_config
            ?.slice()
            ?.sort((a, b) => a.order_index - b.order_index) || [];
        const firstWorkflowStep = sortedWorkflowConfig[0];
        const firstRole = firstWorkflowStep
          ? String(firstWorkflowStep.role).toUpperCase().trim()
          : "";

        // Only show templates where the current user's role is the first step.
        // admin defines who step 1 is — only that role sees the template here.
        if (firstRole && !allowedFirstRoles.includes(firstRole)) {
          return false;
        }

        return true;
      });

      setTemplates(activeTemplates);

      try {
        const confRes = await listSafetyProjectReportConfigs({
          org_id: effectiveOrgId,
        });
        const confList = Array.isArray(confRes?.data)
          ? confRes.data
          : (confRes?.data?.results ?? []);
        const confMap = {};
        const cacheMap = {};
        confList.forEach((c) => {
          confMap[c.project_id] = c;
          if (c.project_id && c.project_code) {
            cacheMap[c.project_id] = c.project_code;
          }
        });
        setProjectConfigs(confMap);
        try {
          localStorage.setItem(
            "SAFETY_PROJECT_REPORT_CONFIGS",
            JSON.stringify(cacheMap),
          );
        } catch { }
      } catch (confErr) {
        console.error("Failed to load project report configs in maker obs:", confErr);
      }
    } catch (err) {
      setTemplates([]);
      showToast(
        err?.response?.data?.detail || "Failed to load safety checklists.",
        "error",
      );
    } finally {
      setTemplatesLoading(false);
    }
  };

  const loadContractorNames = async () => {
    if (!orgId || !projectId || !isInternalMaker) {
      setContractors([]);
      return;
    }

    setContractorsLoading(true);

    try {
      const res = await listContractorNamesByOrg(orgId, projectId);
      const raw = res?.data;

      const list = Array.isArray(raw) ? raw : (raw?.results ?? []);

      setContractors(list);
    } catch (err) {
      setContractors([]);
      showToast(
        err?.response?.data?.detail || "Failed to load contractor names.",
        "error",
      );
    } finally {
      setContractorsLoading(false);
    }
  };

  useEffect(() => {
    if (view === "create" && orgId && projectId) {
      loadCreateTemplates();
    }
  }, [view, orgId, projectId]);

  useEffect(() => {
    if (view === "create" && orgId && projectId && isInternalMaker) {
      loadContractorNames();
    }
  }, [view, orgId, projectId, isInternalMaker]);

  useEffect(() => {
    if (!selectedTemplateId) {
      setSelectedTemplate(null);
      setCreateAnswers({});
      setCreateRemarks({});
      setCreateMedia({});
      setReportMeta({});
      return;
    }

    let alive = true;

    const loadTemplateDetail = async () => {
      setTemplateLoading(true);

      try {
        const res = await getSafetyTemplate(selectedTemplateId);
        const data = res?.data || null;

        if (!alive) return;

        setSelectedTemplate(data);

        const headerMeta = data?.report_header_meta || {};

        setReportMeta(buildInitialReportMeta(data, currentUserProfile));

        const initialAnswers = {};
        const initialRemarks = {};

        (data?.questions || []).forEach((q) => {
          initialAnswers[q.id] = "";
          initialRemarks[q.id] = "";
        });

        setCreateAnswers(initialAnswers);
        setCreateRemarks(initialRemarks);
        setCreateMedia({});
      } catch (err) {
        showToast(
          err?.response?.data?.detail || "Failed to load checklist questions.",
          "error",
        );
      } finally {
        if (alive) setTemplateLoading(false);
      }
    };

    loadTemplateDetail();

    return () => {
      alive = false;
    };
  }, [selectedTemplateId]);

  useEffect(() => {
    if (!selectedTemplate || isInternalMaker) {
      return;
    }

    const contractorField = getTemplateHeaderFields(selectedTemplate).find(
      (field) =>
        field.key === "name_of_contractor" && isEditableMakerField(field),
    );

    if (!contractorField) {
      return;
    }

    const externalContractorName =
      getExternalContractorName(currentUserProfile);

    if (!externalContractorName) {
      return;
    }

    setReportMeta((previous) => ({
      ...previous,
      name_of_contractor: previous.name_of_contractor || externalContractorName,
    }));
  }, [selectedTemplate, currentUserProfile, isInternalMaker]);

  useEffect(() => {
    const ctx = resolveMakerContext();

    setProjectId(ctx.projectId);
    setOrgId(ctx.orgId);
    setCurrentUserProfile(ctx.profile);
  }, []);

  useEffect(() => {
    console.log("MAKER USER PROFILE:", currentUserProfile);
    console.log("IS INTERNAL MAKER:", isInternalMaker);
  }, [currentUserProfile, isInternalMaker]);

  useEffect(() => {
    setObservationPage(1);
  }, [projectId]);

  useEffect(() => {
    fetchList();
  }, [userId, projectId]);

  const openCreateChecklist = () => {
    const ctx = resolveMakerContext();

    if (!ctx.projectId || !ctx.orgId) {
      showToast(
        "Project or organization not found. Please select/open your project profile once.",
        "error",
      );
      return;
    }

    setProjectId(ctx.projectId);
    setOrgId(ctx.orgId);
    setCurrentUserProfile(ctx.profile);

    setSelectedTemplateId("");
    setSelectedTemplate(null);
    setCreateAnswers({});
    setCreateRemarks({});
    setCreateMedia({});
    setReportMeta({});
    setContractors([]);
    setView("create");
  };

  // const backFromCreate = () => {
  //     setSelectedTemplateId("");
  //     setSelectedTemplate(null);
  //     setCreateAnswers({});
  //     setCreateRemarks({});
  //     setCreateMedia({});
  //     setReportMeta({
  //         make_model: "",
  //         identification_no: "",
  //         location: "",
  //         name_of_operator: "",
  //         name_of_contractor: "",
  //     });
  //     setView("dashboard");
  // };

  const backFromCreate = () => {
    setSelectedTemplateId("");
    setSelectedTemplate(null);
    setCreateAnswers({});
    setCreateRemarks({});
    setCreateMedia({});
    setReportMeta({});
    setContractors([]);
    if (onBack) onBack();
    else setView("dashboard");
  };

  // ── derived lists ───────────────────────────────────────────
  const isMakerTurn = (c) =>
    (c.current_assignee_role || "").toUpperCase() === "MAKER" &&
    (!c.current_assignee_id ||
      Number(c.current_assignee_id) === Number(userId));

  const makerInWorkflow = (c) =>
    isObservations ? true : Boolean(c?.maker_in_workflow);

  const rejectedPoints = (c) => Number(c.maker_rejected_points ?? 0);

  const statusOf = (c) => String(c.status || "").toLowerCase();

  const currentRoleOf = (c) =>
    String(c.current_assignee_role || "").toUpperCase();

  const reviewRoundCount = (c) =>
    Number(c?.review_state?.completed_review_rounds ?? 0);

  const isClosedRejected = (c) => statusOf(c) === "closed_rejected";

  const isFinalChecklist = (c) =>
    isObservations
      ? ["approved", "rejected"].includes(statusOf(c))
      : statusOf(c) === "completed" || statusOf(c) === "closed_rejected";

  const isRectificationCycle = (c) =>
    reviewRoundCount(c) > 0 || rejectedPoints(c) > 0;

  const makerCanRectify = (c) =>
    isObservations
      ? isObservationMakerActionable(c, userId)
      : makerInWorkflow(c) && isMakerTurn(c);

  const filteredChecklists = applyChecklistFilters(checklists, filters);

  const makerPendingReview = isObservations
    ? filteredChecklists.filter((c) => {
      const status = statusOf(c);
      const isPendingReviewStage = ["pending_checker", "pending_contractor_checker"].includes(status);
      if (!isPendingReviewStage) return false;
      return isObservationVisibleToMaker(c, userId);
    })
    : checklists.filter(
      (c) =>
        makerInWorkflow(c) &&
        !isFinalChecklist(c) &&
        statusOf(c) === "in_progress" &&
        currentRoleOf(c) === "CHECKER" &&
        !isRectificationCycle(c),
    );

  const makerPendingRectification = isObservations
    ? filteredChecklists.filter((c) => {
      const status = statusOf(c);
      const isPendingRectStage = ["pending_maker", "pending_contractor_maker"].includes(status);
      if (!isPendingRectStage) return false;
      return isObservationVisibleToMaker(c, userId);
    })
    : checklists.filter(
      (c) =>
        makerInWorkflow(c) &&
        !isFinalChecklist(c) &&
        statusOf(c) === "in_progress" &&
        (isRectificationCycle(c) || isMakerTurn(c)),
    );

  const [rectificationPage, setRectificationPage] = useState(1);
  const [reviewPage, setReviewPage] = useState(1);

  useEffect(() => {
    setRectificationPage(1);
    setReviewPage(1);
  }, [filters]);

  const totalRectificationCount = makerPendingRectification.length;
  const totalRectificationPages = Math.max(1, Math.ceil(totalRectificationCount / 10));
  const currentRectificationPage = Math.min(Math.max(1, rectificationPage), totalRectificationPages);

  const paginatedMakerPendingRectification = useMemo(() => {
    if (!isObservations) return makerPendingRectification;
    const start = (currentRectificationPage - 1) * 10;
    return makerPendingRectification.slice(start, start + 10);
  }, [makerPendingRectification, currentRectificationPage, isObservations]);

  const totalReviewCount = makerPendingReview.length;
  const totalReviewPages = Math.max(1, Math.ceil(totalReviewCount / 10));
  const currentReviewPage = Math.min(Math.max(1, reviewPage), totalReviewPages);

  const paginatedMakerPendingReview = useMemo(() => {
    if (!isObservations) return makerPendingReview;
    const start = (currentReviewPage - 1) * 10;
    return makerPendingReview.slice(start, start + 10);
  }, [makerPendingReview, currentReviewPage, isObservations]);

  const observationPagination = useMemo(
    () => ({
      count: totalRectificationCount,
      total_pages: totalRectificationPages,
      current_page: currentRectificationPage,
      page_size: 10,
      next: currentRectificationPage < totalRectificationPages ? currentRectificationPage + 1 : null,
      previous: currentRectificationPage > 1 ? currentRectificationPage - 1 : null,
    }),
    [totalRectificationCount, totalRectificationPages, currentRectificationPage],
  );

  const observationPage = currentRectificationPage;
  const setObservationPage = setRectificationPage;

  // Raw completed list.
  const makerCompletedRaw = isObservations
    ? filteredChecklists.filter((c) => {
      const status = statusOf(c);
      const isFinal = ["approved", "rejected"].includes(status);
      if (!isFinal) return false;
      return isObservationVisibleToMaker(c, userId);
    })
    : checklists.filter((c) => makerInWorkflow(c) && isFinalChecklist(c));

  // Filter options should also come from completed checklists only.
  const contractorOptions = Array.from(
    new Set((isObservations ? checklists : makerCompletedRaw).map(getChecklistContractor).filter(Boolean)),
  ).sort();

  const typeOptions = Array.from(
    new Set((isObservations ? checklists : makerCompletedRaw).map(getChecklistType).filter(Boolean)),
  ).sort();

  // Final completed list after date/contractor/status/type filter.
  const makerCompletedChecklists = isObservations
    ? makerCompletedRaw
    : applyChecklistFilters(
        makerCompletedRaw,
        filters,
      );

  // These counters are affected by filters because they belong to completed bucket.
  const makerApprovedCount = makerCompletedChecklists.filter((item) =>
    isObservations ? statusOf(item) === "approved" : isApprovedChecklist(item),
  ).length;

  const makerRejectedCount = makerCompletedChecklists.filter((item) =>
    isObservations ? statusOf(item) === "rejected" : isRejectedChecklist(item),
  ).length;

  const makerGroupedReports = useMemo(() => {
    if (!isObservations) {
      return [];
    }
    return groupObservationsByReportBatch(checklists);
  }, [isObservations, checklists]);

  const safeCount = (value) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : 0;
  };

  const makerTotalCount =
    safeCount(makerPendingReview?.length) +
    safeCount(makerPendingRectification?.length) +
    safeCount(makerApprovedCount) +
    safeCount(makerRejectedCount);

  const reportNameByDate = useMemo(() => {
    const dateSet = new Set();
    checklists.forEach((item) => {
      if (!item.created_at) return;
      const dateObj = new Date(item.created_at);
      const dateStr = dateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      dateSet.add(dateStr);
    });
    const sortedDates = Array.from(dateSet).sort((a, b) => {
      const [d1, m1, y1] = a.split("/");
      const [d2, m2, y2] = b.split("/");
      return new Date(`${y1}-${m1}-${d1}`) - new Date(`${y2}-${m2}-${d2}`);
    });
    const mapping = {};
    sortedDates.forEach((dateStr, index) => {
      const reportNumber = String(index + 1).padStart(2, "0");
      mapping[dateStr] = `Report AN-ADL-SHOR-${reportNumber}`;
    });
    return mapping;
  }, [checklists]);

  const getItemReportName = (item) => {
    if (!item.created_at) return "";
    const dateObj = new Date(item.created_at);
    const dateStr = dateObj.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    return reportNameByDate[dateStr] ? ` / ${reportNameByDate[dateStr]}` : "";
  };

  const getChecklistDisplayName = (item) => {
    if (isObservations) {
      const observationNo = item?.observation_no ?? item?.id;
      const reportNumber = item?.report_number || "";
      return reportNumber
        ? `Observation #${observationNo} / Report ${reportNumber}`
        : `Observation #${observationNo}`;
    }
    return item.name;
  };

  const getItemContractor = (item) => {
    const raw =
      item?.contractor_name ||
      item?.name_of_contractor ||
      item?.contractor?.name ||
      (typeof item?.contractor === "string" ? item?.contractor : "") ||
      item?.group_name ||
      item?.group?.name ||
      item?.assigned_group_name ||
      item?.safety_report_meta?.name_of_contractor ||
      item?.report_header_meta?.name_of_contractor ||
      "";
    const trimmed = String(raw || "").trim();
    if (!trimmed || trimmed === "—" || trimmed === "-" || trimmed.toLowerCase() === "internal flow") {
      return "Internal";
    }
    return trimmed;
  };

  const getItemInspectionReportNo = (item) => {
    if (isObservations) {
      return item?.report_number || "—";
    }

    const directNo = String(
      item?.inspection_report_no ||
      item?.safety_report_no ||
      item?.report_no ||
      item?.report_number ||
      item?.report_header_meta?.inspection_report_no ||
      item?.safety_report_meta?.inspection_report_no ||
      item?.report_meta?.inspection_report_no ||
      ""
    ).trim();

    if (directNo && !directNo.includes("AUTO")) {
      return directNo.replace(/^Report\s+/i, "");
    }

    if (item?.created_at) {
      const dateObj = new Date(item.created_at);
      const dateStr = dateObj.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      if (reportNameByDate && reportNameByDate[dateStr]) {
        return reportNameByDate[dateStr].replace(/^Report\s+/i, "");
      }
    }

    if (item?.id) {
      return `AN-ADL-SHOR-${String(item.id).padStart(2, "0")}`;
    }

    return "—";
  };

  const ChecklistListMeta = ({ item }) => {
    const contractor = getItemContractor(item);
    const reportNo = getItemInspectionReportNo(item);
    const wing = item?.wing || item?.tower_name || "Not Available";
    const rawCurrentlyWith = item?.currently_with;
    let currentlyWith = "Not Available";
    if (typeof rawCurrentlyWith === "string") {
      currentlyWith = rawCurrentlyWith.trim() || "Not Available";
    } else if (rawCurrentlyWith && typeof rawCurrentlyWith === "object") {
      const name = rawCurrentlyWith.name || rawCurrentlyWith.user_name || "";
      const desig = rawCurrentlyWith.designation || rawCurrentlyWith.group || "";
      if (name && desig) currentlyWith = `${name} (${desig})`;
      else if (name) currentlyWith = name;
      else if (desig) currentlyWith = desig;
    }

    const isInternal =
      !contractor ||
      contractor === "—" ||
      contractor === "-" ||
      contractor.toLowerCase() === "internal" ||
      contractor.toLowerCase() === "internal flow";
    const orgName = getInternalOrganizationName(item);

    return (
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <MetaChip
          icon={<UserRound className="h-3 w-3 shrink-0" />}
          label={isInternal ? null : "Contractor:"}
          value={isInternal ? orgName : contractor}
          tone="blue"
        />

        <MetaChip
          icon={<Building2 className="h-3 w-3 shrink-0" />}
          label="Wing:"
          value={wing}
          tone="slate"
        />

        <MetaChip
          icon={<FileText className="h-3 w-3 shrink-0" />}
          label="Report No.:"
          value={reportNo || "—"}
          tone="slate"
        />

        <MetaChip
          icon={<UserCheck className="h-3 w-3 shrink-0" />}
          label="Currently With:"
          value={currentlyWith}
          tone="slate"
        />
      </div>
    );
  };

  const totalQuestionsLabel = (c) => {
    const n = c.items_count;
    if (n == null || Number.isNaN(Number(n))) return null;
    const t = Number(n);
    return `${t} question${t === 1 ? "" : "s"}`;
  };

  const formatChecklistDateTime = (value) => {
    if (!value) return "";

    const date = new Date(value);

    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getObservationDisplayDateTime = (item) => {
    if (!item) return "";
    if (!isObservations) {
      return item.created_at ? formatChecklistDateTime(item.created_at) : "";
    }

    const latestAttempt =
      Array.isArray(item.attempts) && item.attempts.length > 0
        ? item.attempts[item.attempts.length - 1]
        : null;

    const dateVal =
      item.forwarded_at ||
      item.checker_reviewed_at ||
      latestAttempt?.checker_reviewed_at ||
      item.safety_officer_submitted_at ||
      latestAttempt?.safety_officer_submitted_at ||
      item.updated_at ||
      item.created_at;

    return dateVal ? formatChecklistDateTime(dateVal) : "";
  };

  const rejectedPointsLabel = (c) => {
    const n = rejectedPoints(c);
    if (n <= 0) return null;
    return `${n} rejected point${n === 1 ? "" : "s"}`;
  };

  const MAKER_REJECTED_STATUSES = [
    "rejected_by_checker",
    "rejected_by_supervisor",
  ];

  const makerFixItems =
    detail?.items
      ?.map((item, idx) => {
        let sub = (item.submissions || [])[0];
        const isMock = !sub;

        const currentAssignment = detail?.workflow_assignments?.find(
          (a) => a.order_index === detail?.current_assignee_step_index,
        );
        const assignedQuestions = currentAssignment?.assigned_questions || [];
        const hasAssignedQuestions = assignedQuestions.length > 0;
        const isAssigned =
          !hasAssignedQuestions || assignedQuestions.includes(idx);

        if (!sub) {
          sub = {
            id: `mock-${item.id}`,
            status: "created",
            checklist_item: item.id,
          };
        }

        const editable =
          isAssigned &&
          (sub.status === "created" ||
            MAKER_REJECTED_STATUSES.includes(sub.status));

        return {
          item,
          sub,
          editable,
          isMock,
          highlighted: !isMock && MAKER_REJECTED_STATUSES.includes(sub.status),
        };
      })
      .filter(Boolean) ?? [];

  const makerEditableItems = makerFixItems.filter((row) => row.editable);

  // ── create view shared logic ────────────────────────────────
  const sortedWorkflowConfig =
    selectedTemplate?.workflow_config
      ?.slice()
      ?.sort((a, b) => a.order_index - b.order_index) || [];
  const initialCreateWorkflowStep = sortedWorkflowConfig[0];
  // isCreatorFirstStep: true if the first workflow step role matches the current user's allowed roles.
  // Controls which questions are shown and answered by the initiator.
  const isCreatorFirstStep =
    initialCreateWorkflowStep &&
    allowedFirstRoles.includes(
      String(initialCreateWorkflowStep.role).toUpperCase().trim(),
    );
  const createAssignedQuestionsIndices = isCreatorFirstStep
    ? initialCreateWorkflowStep?.assigned_questions || []
    : [];
  const isCreateAssigned = (idx) => {
    if (!isCreatorFirstStep) return false;
    return (
      createAssignedQuestionsIndices.length === 0 ||
      createAssignedQuestionsIndices.includes(idx)
    );
  };

  // ── navigation ──────────────────────────────────────────────
  const backToDashboard = () => {
    setView("dashboard");
    setDetail(null);
    setMakerRemarks({});
    setMakerMedia({});
    setMakerAnswers({});
    setIsMakerSignatureModalOpen(false);
    setPendingSubmitPayload(null);
    setObservationComment("");
    setObservationPhotos([]);
    setObservationSubmissionRoute("");
    if (sigCanvasRef.current) sigCanvasRef.current.clear();
  };

  // ── open fix view ───────────────────────────────────────────
  const openMakerFix = async (cl) => {
    setDetail(null);
    setDetailLoading(true);
    setMakerRemarks({});
    setMakerMedia({});
    setObservationComment("");
    setObservationPhotos([]);
    setObservationSubmissionRoute("");

    try {
      if (isObservations) {
        const pendingStatuses = ["PENDING_MAKER", "PENDING_CONTRACTOR_MAKER"];

        let data = (await getSafetyObservationById(cl.id))?.data || null;

        if (!data) {
          throw new Error("Safety Observation detail was not returned.");
        }

        if (
          pendingStatuses.includes(String(data?.status || "").toUpperCase()) &&
          Number(data?.maker_id || 0) !== Number(userId)
        ) {
          await claimSafetyObservation(cl.id);

          data = (await getSafetyObservationById(cl.id))?.data || data;
        }

        if (data?.maker_id && Number(data.maker_id) !== Number(userId)) {
          showToast(
            "This Observation has already been claimed by another Maker.",
            "error",
          );
          return;
        }

        setDetail(data);
        const creatorType = String(data?.creator_type || "")
          .trim()
          .toUpperCase()
          .replace(/[\s-]+/g, "_");
        const contractorName = String(
          data?.contractor_name || data?.name_of_contractor || "",
        ).trim();
        const autoRoute =
          isExternalMaker &&
            Boolean(contractorName) &&
            creatorType === "CONTRACTOR_CHECKER"
            ? "CONTRACTOR_CHECKER"
            : "CHECKER";
        setObservationSubmissionRoute(autoRoute);
        setObservationComment(
          data?.attempts?.find(
            (attempt) =>
              Number(attempt?.attempt_no) === Number(data?.current_attempt_no),
          )?.safety_officer_comment || "",
        );

        setView("maker_fix");
        return;
      }

      const res = await getSafetyChecklist(cl.id);
      const data = res?.data || null;
      setDetail(data);

      const initialRemarks = {};
      const initialAnswers = {};

      (data?.items || []).forEach((entry) => {
        const sub = (entry.submissions || [])[0];

        if (!sub) return;

        initialRemarks[sub.id] = "";
        initialAnswers[sub.id] = sub.latest_maker_answer || "";
      });

      setMakerRemarks(initialRemarks);
      setMakerAnswers(initialAnswers);
      setView("maker_fix");
    } catch (err) {
      showToast(
        err?.response?.data?.detail ||
        err?.response?.data?.claim ||
        err?.message ||
        (isObservations
          ? "Failed to load Safety Observation."
          : "Failed to load checklist"),
        "error",
      );
    } finally {
      setDetailLoading(false);
    }
  };

  // ── submit all fixes at once ────────────────────────────────
  // const handleSubmitAllFixes = async () => {
  //     if (!detail) return;
  //     const submissions = (detail.items || [])
  //         .map((item) => {
  //             const sub = (item.submissions || []).find((s) =>
  //                 MAKER_QUEUE_STATUSES.includes(s.status)
  //             );
  //             if (!sub) return null;
  //             const payload = { submission_id: sub.id, maker_remarks: makerRemarks[sub.id] ?? "" };
  //             const media = makerMedia[sub.id];
  //             if (media) payload.maker_media_base64 = media;
  //             return payload;
  //         })
  //         .filter(Boolean);
  //     const payload = submissions.length ? { submissions } : {};
  //     const isBulk = !submissions.length;
  //     if (shouldShowMakerSignatureModal(detail, submissions, isBulk)) {
  //         setPendingSubmitPayload(payload);
  //         setIsMakerSignatureModalOpen(true);
  //         return;
  //     }

  //     setSubmitting(true);
  //     try {
  //         await submitSafetyChecklist(detail.id, payload);
  //         showToast("Submitted successfully", "success");
  //         backToDashboard();
  //         fetchList();
  //     } catch (err) {
  //         showToast(err?.response?.data?.detail || "Submit failed", "error");
  //     } finally {
  //         setSubmitting(false);
  //     }
  // };

  // ── submit a single fix ─────────────────────────────────────

  const handleSubmitAllFixes = async () => {
    if (!detail) return;

    if (isObservations) {
      if (!String(observationComment || "").trim()) {
        showToast("Please Enter Rectification Remarks.", "error");
        return;
      }

      if (!observationPhotos.length) {
        showToast(
          "Please attach at least one closure photograph.",
          "error",
        );
        return;
      }

      setPendingSubmitPayload({
        observation: true,
      });
      setSignaturePurpose("submit_fix");
      setIsMakerSignatureModalOpen(true);
      return;
    }

    const submissions = [];
    const answers = {};
    const answersMedia = [];

    makerFixItems
      .filter(({ editable }) => editable)
      .forEach(({ sub, item, isMock }) => {
        const ansKey = isMock ? `mock-${item.id}` : sub.id;
        const ansData = makerAnswers[ansKey] || makerAnswers[sub.id];
        const answer =
          typeof ansData === "object" ? ansData?.answer || "" : ansData || "";
        const secondaryAnswer =
          typeof ansData === "object" ? ansData?.secondary_answer || "" : "";

        if (isMock) {
          answers[item.id] = {
            answer: answer,
            secondary_answer: secondaryAnswer,
            type: item.input_type || item.type || "multiple_choice",
          };
          if (makerMedia[ansKey]) {
            answersMedia.push({ id: item.id, file: makerMedia[ansKey] });
          }
        } else {
          submissions.push({
            submission_id: sub.id,
            answer: answer,
            secondary_answer: secondaryAnswer,
            maker_remarks: makerRemarks[sub.id] ?? "",
            type: item.input_type || item.type || "multiple_choice",
          });
        }
      });

    const hasMissingAnswer =
      submissions.some((s) => s.type !== "file_upload" && !s.answer) ||
      Object.values(answers).some(
        (a) =>
          a.type !== "file_upload" && (typeof a === "object" ? !a.answer : !a),
      );

    if (!submissions.length && Object.keys(answers).length === 0) {
      showToast("No questions to submit.", "error");
      return;
    }

    if (hasMissingAnswer) {
      showToast("Please answer all required questions.", "error");
      return;
    }

    const payload = {};
    if (submissions.length) payload.submissions = submissions;
    if (Object.keys(answers).length) payload.answers = answers;
    if (answersMedia.length) payload.answersMedia = answersMedia;

    // IMPORTANT:
    // Re-submit must always go through maker signature modal.
    // Do not directly call submitSafetyChecklist here.
    setPendingSubmitPayload(payload);
    setSignaturePurpose("submit_fix");
    setIsMakerSignatureModalOpen(true);
  };

  // const handleSubmitSingleFix = async (submissionId) => {
  //     if (!detail) return;

  //     const submissions = [];

  //     (detail.items || []).forEach((item) => {
  //         const sub = (item.submissions || []).find((s) => s.id === submissionId);
  //         if (!sub) return;

  //         submissions.push({
  //             submission_id: sub.id,
  //             answer: makerAnswers[sub.id] || "",
  //             maker_remarks: makerRemarks[sub.id] ?? "",
  //         });
  //     });

  //     if (!submissions.length) {
  //         showToast("Could not find fix to submit.", "error");
  //         return;
  //     }

  //     if (!submissions[0]?.answer) {
  //         showToast("Please answer this question before submitting.", "error");
  //         return;
  //     }

  //     // IMPORTANT:
  //     // Single fix submit also needs maker signature.
  //     setPendingSubmitPayload({ submissions });
  //     setSignaturePurpose("submit_fix");
  //     setIsMakerSignatureModalOpen(true);
  // };

  const handleCloseMakerSignatureModal = () => {
    if (signatureSubmitting) return;

    setIsMakerSignatureModalOpen(false);
    setPendingSubmitPayload(null);
    setPendingCreateSubmitFormData(null);
    setSignaturePurpose(null);

    if (sigCanvasRef.current) sigCanvasRef.current.clear();
  };

  const handleSubmitWithMakerSignature = async () => {
    if (!detail || !pendingSubmitPayload) return;

    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
      showToast("Please provide your signature.", "error");
      return;
    }

    const dataUrl = sigCanvasRef.current.toDataURL("image/png");
    const base64Part = dataUrl.split(",")[1] || "";

    if (!base64Part) {
      showToast("Could not capture signature. Try again.", "error");
      return;
    }

    const binary = atob(base64Part);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }

    const signatureFile = new File(
      [bytes],
      `maker-signature-${detail.id}-${Date.now()}.png`,
      { type: "image/png" },
    );

    if (isObservations) {
      const formData = new FormData();

      formData.append("comment", String(observationComment || "").trim());
      formData.append("signature", signatureFile);

      const finalSubmissionRoute =
        observationSubmissionRoute || autoAssignedObservationRoute;
      formData.append("submission_route", finalSubmissionRoute);

      observationPhotos.forEach((file, index) => {
        formData.append(`photographs[${index}][image]`, file);
        formData.append(
          `photographs[${index}][comment]`,
          String(observationComment || "").trim(),
        );
      });

      setSignatureSubmitting(true);

      try {
        await submitSafetyObservationRectification(detail.id, formData);

        showToast(
          finalSubmissionRoute === "CONTRACTOR_CHECKER"
            ? "Corrective action submitted to Contractor Checker."
            : "Observation Rectification Submitted.",
          "success",
        );

        setIsMakerSignatureModalOpen(false);
        setPendingSubmitPayload(null);
        setSignaturePurpose(null);
        setObservationComment("");
        setObservationPhotos([]);
        setObservationSubmissionRoute("");

        if (sigCanvasRef.current) {
          sigCanvasRef.current.clear();
        }

        backToDashboard();
        fetchList();
      } catch (err) {
        showToast(
          err?.response?.data?.detail ||
          err?.response?.data?.status ||
          err?.response?.data?.submission_route?.[0] ||
          "Observation submission failed.",
          "error",
        );
      } finally {
        setSignatureSubmitting(false);
      }

      return;
    }

    const formData = new FormData();

    if (pendingSubmitPayload?.submissions?.length) {
      formData.append(
        "submissions",
        JSON.stringify(pendingSubmitPayload.submissions),
      );

      pendingSubmitPayload.submissions.forEach((sub) => {
        const file = makerMedia[sub.submission_id];

        if (file) {
          // Preferred explicit key, backend supports this.
          formData.append(`maker_media_${sub.submission_id}`, file);
        }
      });
    }

    if (pendingSubmitPayload?.answers) {
      formData.append("answers", JSON.stringify(pendingSubmitPayload.answers));
    }

    if (pendingSubmitPayload?.answersMedia) {
      pendingSubmitPayload.answersMedia.forEach(({ id, file }) => {
        formData.append(`answers_media_${id}`, file);
      });
    }

    formData.append("maker_signature", signatureFile);

    setSignatureSubmitting(true);

    try {
      await submitSafetyChecklist(detail.id, formData);

      showToast("Submitted successfully", "success");

      setIsMakerSignatureModalOpen(false);
      setPendingSubmitPayload(null);
      setSignaturePurpose(null);

      if (sigCanvasRef.current) sigCanvasRef.current.clear();

      backToDashboard();
      fetchList();
    } catch (err) {
      showToast(
        err?.response?.data?.detail || "Signature submit failed",
        "error",
      );
    } finally {
      setSignatureSubmitting(false);
    }
  };

  const getBlobErrorMessage = async (response, fallbackMessage) => {
    const blob = response?.data;
    const contentType = response?.headers?.["content-type"] || blob?.type || "";

    if (
      !contentType.includes("application/json") ||
      typeof blob?.text !== "function"
    ) {
      return fallbackMessage;
    }

    try {
      const text = await blob.text();
      const parsed = JSON.parse(text);
      return parsed?.detail || fallbackMessage;
    } catch {
      return fallbackMessage;
    }
  };

  const isMobileDevice = () => {
    if (typeof navigator === "undefined") return false;
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  };

  const getAccessTokenForMobileDownload = () =>
    localStorage.getItem("ACCESS_TOKEN") ||
    localStorage.getItem("access") ||
    localStorage.getItem("accessToken") ||
    "";

  const getChecklistServiceBaseUrl = () => {
    const isLocal =
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "localhost";

    return isLocal
      ? "http://127.0.0.1:8001"
      : "https://konstruct.world/checklists";
  };

  const safePdfFileName = (name = "report") => {
    return (
      String(name || "report")
        .trim()
        .replace(/[^\w.-]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "") || "report"
    );
  };

  const sendDownloadToFlutter = ({ url, fileName, token }) => {
    const payload = JSON.stringify({
      url,
      fileName,
      token,
      mimeType: "application/pdf",
    });

    if (window.flutter_inappwebview?.callHandler) {
      window.flutter_inappwebview.callHandler("downloadFile", payload);
      return true;
    }

    if (window.DownloadChannel?.postMessage) {
      window.DownloadChannel.postMessage(payload);
      return true;
    }

    return false;
  };

  const handleDownloadObservationReport = async (report) => {
    if (!report) return;

    const mobile = isMobileDevice();
    const token = getAccessTokenForMobileDownload();
    const checklistBaseUrl = getChecklistServiceBaseUrl();

    const firstItem = report.items?.[0] || {};
    const effectiveOrgId =
      firstItem.org_id ||
      orgId ||
      resolveOrgId?.() ||
      null;
    const effectiveProjectId =
      firstItem.project_id ||
      projectId ||
      resolveActiveProjectId?.() ||
      null;
    const groupedIds = (report.items || [])
      .map((entry) => entry?.id)
      .filter(Boolean);

    showToast("Generating Observation Report PDF...", "success");

    /* MOBILE WEBVIEW / FLUTTER FLOW */
    if (mobile) {
      try {
        const query = new URLSearchParams({
          mode: "download",
        });
        if (report.reportBatchId) {
          query.set("report_batch_id", report.reportBatchId);
        } else if (report.reportNumber) {
          query.set("report_number", report.reportNumber);
        }
        const effectiveDate =
          report.reportDate || report.date || report.displayDate || undefined;
        if (effectiveDate) query.set("date", effectiveDate);
        if (effectiveOrgId) query.set("org_id", effectiveOrgId);
        if (effectiveProjectId) query.set("project_id", effectiveProjectId);
        if (groupedIds.length > 0) query.set("ids", groupedIds.join(","));

        const reportUrl = `${checklistBaseUrl}/safety/observations/report/?${query.toString()}`;
        const fileName = `${safePdfFileName(
          report.reportName ||
          (report.reportNumber
            ? `Observation-Report-${report.reportNumber}`
            : `Observation-Report-${report.displayDate || report.reportDate || "Report"}`),
        )}.pdf`;

        const sentToFlutter = sendDownloadToFlutter({
          url: reportUrl,
          fileName,
          token,
        });

        if (sentToFlutter) {
          showToast("Downloading report...", "success");
          return;
        }

        window.open(reportUrl, "_blank");
        return;
      } catch (err) {
        console.error("Mobile download error:", err);
      }
    }

    /* DESKTOP / WEB FLOW */
    try {
      const res = await downloadSafetyObservationReport({
        report_batch_id: report.reportBatchId || undefined,
        report_number: report.reportNumber || undefined,
        date: report.reportDate || report.date || report.displayDate || undefined,
        org_id: effectiveOrgId,
        project_id: effectiveProjectId,
        ids: groupedIds.length > 0 ? groupedIds.join(",") : undefined,
        t: new Date().getTime(),
      });

      const blob = res?.data;
      const contentType = res?.headers?.["content-type"] || blob?.type || "";

      if (!blob || blob.size === 0 || contentType.includes("application/json")) {
        const message = await getBlobErrorMessage(res, "Failed to generate PDF");
        showToast(message, "error");
        return;
      }

      const disposition = res?.headers?.["content-disposition"];
      const fallbackName = `${safePdfFileName(
        report.reportName ||
        (report.reportNumber
          ? `Observation-Report-${report.reportNumber}`
          : `Observation-Report-${report.displayDate || report.reportDate || "Report"}`),
      )}.pdf`;

      downloadBlob(
        blob instanceof Blob ? blob : new Blob([blob], { type: "application/pdf" }),
        filenameFromDisposition(disposition) || fallbackName,
      );

      showToast("Report downloaded successfully", "success");
    } catch (err) {
      let errorMsg = "Failed to generate PDF";
      if (err?.code === "ECONNABORTED" || err?.message?.includes("timeout")) {
        errorMsg = "Download timed out. Please try again.";
      } else {
        try {
          if (err?.response?.data instanceof Blob) {
            const text = await err.response.data.text();
            const parsed = JSON.parse(text);
            errorMsg = parsed?.detail || parsed?.message || errorMsg;
          } else if (err?.response?.data?.detail) {
            errorMsg = err.response.data.detail;
          } else if (err?.message) {
            errorMsg = err.message;
          }
        } catch {
          // fallback
        }
      }
      showToast(errorMsg, "error");
    }
  };

  const handleDownloadReport = async (item) => {
    if (!item?.id) return;

    if (isObservations) {
      let reportGroup = (makerGroupedReports || []).find(
        (r) =>
          (item.reportBatchId && r.reportBatchId === item.reportBatchId) ||
          (item.report_batch_id && r.reportBatchId === item.report_batch_id) ||
          (item.reportNumber && r.reportNumber === item.reportNumber) ||
          (item.report_number && r.reportNumber === item.report_number) ||
          r.firstItemId === item.id ||
          r.items?.some((entry) => entry?.id === item.id),
      );

      if (!reportGroup) {
        const matchingItems = (checklists || []).filter(
          (c) =>
            (item.report_batch_id && c.report_batch_id === item.report_batch_id) ||
            (item.report_number && c.report_number === item.report_number) ||
            c.id === item.id,
        );
        const reportItems = matchingItems.length > 0 ? matchingItems : [item];
        const first = reportItems[0] || item;
        const repNo = first.report_number || item.report_number || "";
        const repDate = formatReportDisplayDate(
          first.report_date || item.report_date,
          first.created_at || item.created_at,
        );
        reportGroup = {
          reportBatchId: first.report_batch_id || item.report_batch_id || undefined,
          reportNumber: repNo || undefined,
          reportDate: first.report_date || item.report_date || undefined,
          displayDate: repDate,
          reportName: repNo
            ? `Observation Report - ${repNo}`
            : `Observation Report - ${repDate || "Report"}`,
          items: reportItems,
          firstItemId: first.id,
        };
      }

      await handleDownloadObservationReport(reportGroup);
      return;
    }

    try {
      const res = await downloadSafetyReport(item.id, {
        org_id: orgId,
        mode: "download",
      });

      const blob = res?.data;
      if (!blob) {
        showToast("Report file not found.", "error");
        return;
      }

      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" }),
      );
      const link = document.createElement("a");

      const safeName = String(item.name || "safety-report")
        .trim()
        .replace(/[^\w.-]+/g, "-");

      link.href = url;
      link.download = `${safeName}-${item.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
      showToast("Report downloaded", "success");
    } catch (err) {
      showToast(
        err?.response?.data?.detail || err?.message || "Download failed",
        "error",
      );
    }
  };

  const handleCreateAndSubmitChecklist = async () => {
    if (!selectedTemplate) {
      showToast("Please select checklist.", "error");
      return;
    }

    const editableHeaderFields =
      getTemplateHeaderFields(selectedTemplate).filter(isEditableMakerField);

    const cleanReportMeta = {};

    editableHeaderFields.forEach((field) => {
      let value = String(
        reportMeta[field.key] ?? field.default_value ?? "",
      ).trim();

      if (field.key === "name_of_contractor" && !isInternalMaker) {
        value = value || getExternalContractorName(currentUserProfile);
      }

      cleanReportMeta[field.key] = value;
    });

    const missingRequiredField = editableHeaderFields.find(
      (field) =>
        isRequiredMakerField(field) &&
        !String(cleanReportMeta[field.key] || "").trim(),
    );

    if (missingRequiredField) {
      showToast(`${missingRequiredField.label} is required.`, "error");
      return;
    }

    const questions = selectedTemplate?.questions || [];

    if (!questions.length) {
      showToast("Selected checklist has no questions.", "error");
      return;
    }

    const assignedQuestionsToAnswer = questions.filter((q, idx) =>
      isCreateAssigned(idx),
    );

    const unansweredIndex = assignedQuestionsToAnswer.findIndex((q) => {
      const type = q.input_type || q.type || "multiple_choice";
      if (type === "file_upload") return false;
      const ansObj = createAnswers[q.id];
      const ansText =
        typeof ansObj === "object" && ansObj !== null ? ansObj.answer : ansObj;
      return !String(ansText || "").trim();
    });

    if (unansweredIndex !== -1) {
      const unanswered = assignedQuestionsToAnswer[unansweredIndex];

      showToast(`Please answer Question: ${unanswered.text}`, "error");
      return;
    }

    const missingRequiredPhoto = assignedQuestionsToAnswer.find(
      (q) => q.photo_required && !createMedia[q.id],
    );

    if (missingRequiredPhoto) {
      showToast(`Photo is required for: ${missingRequiredPhoto.text}`, "error");
      return;
    }

    const formData = new FormData();

    formData.append("template_id", Number(selectedTemplate.id));
    formData.append("project_id", Number(projectId));
    formData.append("org_id", Number(orgId));
    formData.append("title", selectedTemplate.title || "Safety Checklist");

    // formData.append(
    //     "report_meta",
    //     JSON.stringify({
    //         make_model: reportMeta.make_model || "",
    //         identification_no: reportMeta.identification_no || "",
    //         location: reportMeta.location || "",
    //         name_of_operator: reportMeta.name_of_operator || "",

    //         name_of_contractor: reportMeta.name_of_contractor || "",
    //     })
    // );

    formData.append("report_meta", JSON.stringify(cleanReportMeta));

    formData.append(
      "answers",
      JSON.stringify(
        questions.map((q) => {
          const ansData = createAnswers[q.id];
          const answer =
            typeof ansData === "object" ? ansData?.answer || "" : ansData || "";
          const secondaryAnswer =
            typeof ansData === "object" ? ansData?.secondary_answer || "" : "";
          return {
            template_question_id: q.id,
            answer: answer,
            secondary_answer: secondaryAnswer,
            maker_remarks: createRemarks[q.id] || "",
          };
        }),
      ),
    );

    questions.forEach((q) => {
      const file = createMedia[q.id];
      if (file) {
        formData.append(`maker_media_${q.id}`, file);
      }
    });

    setSignaturePurpose("create");
    setPendingCreateSubmitFormData(formData);
    setIsMakerSignatureModalOpen(true);
  };

  const handleCreateSubmitWithMakerSignature = async () => {
    if (!pendingCreateSubmitFormData) return;

    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
      showToast("Please provide your signature.", "error");
      return;
    }

    const dataUrl = sigCanvasRef.current.toDataURL("image/png");
    const base64Part = dataUrl.split(",")[1] || "";

    const binary = atob(base64Part);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }

    const signatureFile = new File(
      [bytes],
      `maker-create-signature-${Date.now()}.png`,
      { type: "image/png" },
    );

    pendingCreateSubmitFormData.append("maker_signature", signatureFile);

    setSignatureSubmitting(true);

    try {
      await createAndSubmitSafetyChecklist(pendingCreateSubmitFormData);

      showToast("Checklist submitted to safety officers.", "success");

      setIsMakerSignatureModalOpen(false);
      setPendingCreateSubmitFormData(null);
      setSignaturePurpose(null);

      if (sigCanvasRef.current) sigCanvasRef.current.clear();

      backFromCreate();
      fetchList();
    } catch (err) {
      showToast(
        err?.response?.data?.detail || "Failed to submit checklist.",
        "error",
      );
    } finally {
      setSignatureSubmitting(false);
    }
  };

  const handleMakerSignatureSubmit = async () => {
    if (signaturePurpose === "create") {
      await handleCreateSubmitWithMakerSignature();
    } else {
      await handleSubmitWithMakerSignature();
    }
  };

  // ── render ───────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50/60 p-4 sm:p-6 lg:p-8">
      <div
        className={`mx-auto w-full ${view === "dashboard" ? "max-w-[1600px] space-y-6" : "max-w-5xl"
          }`}
      >
        {!userId && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            User not found in session. Log in or ensure USER_DATA is set in
            localStorage.
          </div>
        )}

        {loading && (
          <div className="py-8 text-center text-muted-foreground">Loading…</div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* ═══ VIEW: CREATE CHECKLIST ═══ */}
            {view === "create" && !isObservations && (
              <section>
                <button
                  type="button"
                  onClick={backFromCreate}
                  className="mb-4 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Dashboard
                </button>

                <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100">
                      <ClipboardList className="h-5 w-5 text-orange-500" />
                    </div>
                    <div>
                      <h1 className="text-lg font-bold text-foreground sm:text-xl">
                        Start Safety Checklist
                      </h1>
                      <p className="text-sm text-muted-foreground">
                        Choose the checklist to start the inspection.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm">
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Checklists
                  </label>

                  <select
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    disabled={templatesLoading}
                    className="h-11 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                  >
                    <option value="">
                      {templatesLoading
                        ? "Loading checklists..."
                        : "Select checklist"}
                    </option>

                    {templates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>
                        {tpl.title}
                      </option>
                    ))}
                  </select>

                  {!templatesLoading && templates.length === 0 && (
                    <p className="mt-2 text-xs text-red-500">
                      No active Safety Checklist formats found for this project.
                    </p>
                  )}
                </div>

                {templateLoading && (
                  <div className="rounded-xl border bg-card p-5 text-sm text-muted-foreground">
                    Loading questions...
                  </div>
                )}

                {!templateLoading && selectedTemplate && (
                  <>
                    <div className="mb-4 rounded-xl border bg-card p-5 shadow-sm">
                      <h2 className="text-base font-bold text-foreground">
                        {selectedTemplate.title}
                      </h2>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {
                          (selectedTemplate.questions || []).filter((q, idx) =>
                            isCreateAssigned(idx),
                          ).length
                        }{" "}
                        question(s)
                      </p>
                    </div>

                    <ReportHeaderCreateCard
                      selectedTemplate={selectedTemplate}
                      reportMeta={reportMeta}
                      setReportMeta={setReportMeta}
                      projectId={projectId}
                      projectConfigs={projectConfigs}
                      currentUserProfile={currentUserProfile}
                      isInternalMaker={isInternalMaker}
                      contractors={contractors}
                      contractorsLoading={contractorsLoading}
                    />

                    {/* <div className="mb-4 rounded-xl border bg-card p-5 shadow-sm">
                                            <h3 className="mb-4 text-sm font-semibold text-foreground">
                                                Inspection Details
                                            </h3>

                                            {isInternalMaker && (
                                                <div className="mb-4">
                                                    <p className="mt-1 text-xs text-gray-500">
                                                        If no contractor is selected, organization name will be used in the report.
                                                    </p>
                                                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                                        Contractor Name
                                                    </label>

                                                    <select
                                                        value={reportMeta.name_of_contractor}
                                                        onChange={(e) =>
                                                            setReportMeta((prev) => ({
                                                                ...prev,
                                                                name_of_contractor: e.target.value,
                                                            }))
                                                        }
                                                        disabled={contractorsLoading}
                                                        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                                                    >
                                                        <option value="">
                                                            {contractorsLoading
                                                                ? "Loading contractors..."
                                                                : "Use organization name"}
                                                        </option>

                                                        {contractors.map((contractor) => {
                                                            const name =
                                                                typeof contractor === "string"
                                                                    ? contractor
                                                                    : contractor?.name || contractor?.contractor_name || "";

                                                            if (!name) return null;

                                                            return (
                                                                <option key={name} value={name}>
                                                                    {name}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>

                                                    {!contractorsLoading && contractors.length === 0 && (
                                                        <p className="mt-1 text-xs text-red-500">
                                                            No contractor found in this project.
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                <div>
                                                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                                        Make / Model
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={reportMeta.make_model}
                                                        onChange={(e) =>
                                                            setReportMeta((prev) => ({
                                                                ...prev,
                                                                make_model: e.target.value,
                                                            }))
                                                        }
                                                        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                                                        placeholder="Enter make / model"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                                        Identification No.
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={reportMeta.identification_no}
                                                        onChange={(e) =>
                                                            setReportMeta((prev) => ({
                                                                ...prev,
                                                                identification_no: e.target.value,
                                                            }))
                                                        }
                                                        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                                                        placeholder="Enter identification no."
                                                    />
                                                </div>

                                                <div>
                                                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                                        Location
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={reportMeta.location}
                                                        onChange={(e) =>
                                                            setReportMeta((prev) => ({
                                                                ...prev,
                                                                location: e.target.value,
                                                            }))
                                                        }
                                                        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                                                        placeholder="Enter location"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                                        Name of Operator
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={reportMeta.name_of_operator}
                                                        onChange={(e) =>
                                                            setReportMeta((prev) => ({
                                                                ...prev,
                                                                name_of_operator: e.target.value,
                                                            }))
                                                        }
                                                        className="h-10 w-full rounded-lg border border-border bg-white px-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                                                        placeholder="Enter operator name"
                                                    />
                                                </div>
                                            </div>
                                        </div> */}

                    {!isCreatorFirstStep && (
                      <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                        The first step of this checklist workflow is assigned to
                        another role. Please complete the Inspection Details
                        above and click "Submit Checklist" to initiate the
                        process.
                      </div>
                    )}
                    <div className="space-y-4">
                      {(selectedTemplate.questions || []).map((q, idx) => {
                        if (!isCreateAssigned(idx)) return null;

                        return (
                          <div
                            key={q.id}
                            className="rounded-xl border bg-card p-5 shadow-sm transition-shadow hover:shadow-md"
                          >
                            <div className="mb-4 flex items-start gap-3">
                              <QuestionBadge number={idx + 1} />
                              <div>
                                <p className="pt-1 text-sm font-semibold text-foreground sm:text-base">
                                  {q.text}
                                </p>

                                {q.description && (
                                  <p className="mt-1 text-xs text-muted-foreground">
                                    {q.description}
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="space-y-3">
                              <DynamicSafetyInput
                                item={q}
                                value={
                                  createAnswers[q.id]?.answer ??
                                  (typeof createAnswers[q.id] === "string"
                                    ? createAnswers[q.id]
                                    : "")
                                }
                                secondaryValue={
                                  createAnswers[q.id]?.secondary_answer ?? ""
                                }
                                onChange={(value) =>
                                  setCreateAnswers((prev) => ({
                                    ...prev,
                                    [q.id]: { ...prev[q.id], answer: value },
                                  }))
                                }
                                onSecondaryChange={(value) =>
                                  setCreateAnswers((prev) => ({
                                    ...prev,
                                    [q.id]: {
                                      ...prev[q.id],
                                      secondary_answer: value,
                                    },
                                  }))
                                }
                              />

                              <div>
                                <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                  Remark / Comment
                                </label>
                                <textarea
                                  rows={1}
                                  value={createRemarks[q.id] || ""}
                                  onChange={(e) => {
                                    setCreateRemarks((prev) => ({
                                      ...prev,
                                      [q.id]: e.target.value,
                                    }));

                                    autoResizeTextarea(e.target);
                                  }}
                                  onInput={(e) => autoResizeTextarea(e.target)}
                                  className="min-h-[42px] w-full resize-none overflow-hidden rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                                  placeholder="Add comment if you want to reject this question..."
                                />
                              </div>

                              <PhotoUploadArea
                                id={`create-maker-${q.id}`}
                                previewBase64={createMedia[q.id]}
                                label={
                                  q.photo_required
                                    ? "Upload photo (required)"
                                    : "Upload photo (optional)"
                                }
                                onFileChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;

                                  setCreateMedia((prev) => ({
                                    ...prev,
                                    [q.id]: file,
                                  }));
                                }}
                                onRemove={() =>
                                  setCreateMedia((prev) => ({
                                    ...prev,
                                    [q.id]: undefined,
                                  }))
                                }
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mt-6">
                      <button
                        type="button"
                        onClick={handleCreateAndSubmitChecklist}
                        disabled={submitting}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-3.5 text-sm font-bold text-white shadow-lg shadow-orange-200 transition-all hover:shadow-xl hover:shadow-orange-300 disabled:opacity-60"
                      >
                        <Send className="h-4 w-4" />
                        {submitting ? "Submitting..." : "Submit Checklist"}
                      </button>
                    </div>
                  </>
                )}
              </section>
            )}

            {/* ═══ VIEW: MAKER FIX ═══ */}
            {view === "maker_fix" &&
              detail &&
              (isObservations ? (
                <section>
                  <button
                    type="button"
                    onClick={backToDashboard}
                    className="mb-4 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Dashboard
                  </button>

                  <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100">
                        <Wrench className="h-5 w-5 text-orange-500" />
                      </div>
                      <div>
                        <h1 className="text-lg font-bold text-foreground sm:text-xl">
                          Rectify Safety Observation #{detail.observation_no ?? detail.id}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                          Rectify the observation, attach corrective-action
                          evidence and submit it for review.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Inspection Details Header (Safety Inspection Theme) */}
                    <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
                      <div className="mb-4">
                        <h3 className="text-sm font-semibold text-slate-900">
                          Inspection Details
                        </h3>
                        <p className="mt-1 text-xs text-slate-500">
                          Complete the inspection details below before answering the checklist questions. Required fields marked with * must be filled and will be included in the generated report.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {/* Row 1 */}
                        <ReadOnlyHeaderField
                          label="FORMAT NO."
                          value={
                            detail.format_no ||
                            detail.report_header_meta?.format_no ||
                            "ADL-OH&S-F012"
                          }
                        />

                        <ReadOnlyHeaderField
                          label="REVISION NO."
                          value={
                            detail.revision_no ||
                            detail.report_header_meta?.revision_no ||
                            "R01"
                          }
                        />

                        <ReadOnlyHeaderField
                          label="ISSUED DATE"
                          value={
                            detail.issued_date ||
                            detail.report_header_meta?.issued_date ||
                            "01-09-2025"
                          }
                        />

                        {/* Row 2 */}
                        <ReadOnlyHeaderField
                          label="REVISION DATE"
                          value={
                            detail.revision_date ||
                            detail.report_header_meta?.revision_date ||
                            "—"
                          }
                        />

                        <ReadOnlyHeaderField
                          label="PROJECT"
                          value={
                            detail.project_name ||
                            detail.project?.name ||
                            detail.project_title ||
                            (projectId ? `Project ${projectId}` : "—")
                          }
                        />

                        <ReadOnlyHeaderField
                          label="INSPECTION REPORT NO."
                          value={
                            detail.report_number ||
                            detail.inspection_report_no ||
                            detail.report_no ||
                            "—"
                          }
                        />

                        {/* Row 3 */}
                        <ReadOnlyHeaderField
                          label="DATE OF INSPECTION"
                          value={formatObservationInspectionDate(
                            detail.date_of_inspection ||
                            detail.inspection_date ||
                            detail.created_at
                          )}
                        />
                      </div>
                    </div>

                    <div className="rounded-xl border bg-card p-5 shadow-sm">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Unsafe Act / Condition
                      </p>
                      <p className="font-semibold text-foreground">
                        {detail.unsafe_act_condition_category || "-"}
                      </p>
                      <p className="mt-2 text-sm text-muted-foreground">
                        {detail.unsafe_act_condition_description || "-"}
                      </p>
                    </div>

                    <div className="rounded-xl border bg-card p-5 shadow-sm">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Location
                      </p>
                      <p className="text-sm font-medium text-foreground">
                        {detail.location_combined || "-"}
                      </p>
                    </div>

                    <div className="rounded-xl border bg-card p-5 shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Uploaded Observation Photograph By Checker
                        </p>
                        {Array.isArray(detail.initial_photographs) && detail.initial_photographs.length > 1 && (
                          <span className="text-xs font-semibold text-primary bg-primary/10 px-2.5 py-0.5 rounded-full">
                            {detail.initial_photographs.length} Photos
                          </span>
                        )}
                      </div>
                      {Array.isArray(detail.initial_photographs) && detail.initial_photographs.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {detail.initial_photographs.map((photoItem, pIdx) => {
                            const pUrl = getObservationPhotoUrl(photoItem);
                            return (
                              <div key={photoItem.id || pIdx} className="space-y-2 rounded-xl border border-border bg-muted/10 p-3">
                                <div className="relative overflow-hidden rounded-lg border border-border bg-muted/20">
                                  {pUrl ? (
                                    <img
                                      src={pUrl}
                                      alt={`Observation Photo ${pIdx + 1}`}
                                      className="max-h-56 w-full object-contain cursor-pointer transition hover:opacity-95"
                                      onClick={() => window.open(pUrl, "_blank")}
                                    />
                                  ) : (
                                    <div className="flex h-36 items-center justify-center text-xs italic text-muted-foreground">
                                      Photo unavailable
                                    </div>
                                  )}
                                  <span className="absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white">
                                    #{pIdx + 1}
                                  </span>
                                </div>
                                {photoItem.comment && (
                                  <div className="rounded-md border border-border/60 bg-muted/30 p-2 text-xs text-foreground">
                                    <span className="font-semibold text-muted-foreground uppercase tracking-wide">
                                      Comment:
                                    </span>{" "}
                                    {photoItem.comment}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : getObservationPhotoUrl(
                        detail.photograph_of_unsafe_act ||
                        detail.photo_of_unsafe_act ||
                        detail.observation_photo ||
                        detail.photo ||
                        detail.photograph
                      ) ? (
                        <div className="space-y-3">
                          <div className="overflow-hidden rounded-xl border border-border bg-muted/20">
                            <img
                              src={getObservationPhotoUrl(
                                detail.photograph_of_unsafe_act ||
                                detail.photo_of_unsafe_act ||
                                detail.observation_photo ||
                                detail.photo ||
                                detail.photograph
                              )}
                              alt="Observation by Checker"
                              className="max-h-72 w-full object-contain cursor-pointer transition hover:opacity-95"
                              onClick={() => {
                                const url = getObservationPhotoUrl(
                                  detail.photograph_of_unsafe_act ||
                                  detail.photo_of_unsafe_act ||
                                  detail.observation_photo ||
                                  detail.photo ||
                                  detail.photograph
                                );
                                if (url) window.open(url, "_blank");
                              }}
                            />
                          </div>
                          {(detail.photograph_of_unsafe_act_comment ||
                            detail.observation_photo_comment ||
                            detail.photo_comment) && (
                              <div className="rounded-lg border border-border/60 bg-muted/30 p-2.5 text-xs text-foreground">
                                <span className="font-semibold text-muted-foreground uppercase tracking-wide">
                                  Checker Comment:
                                </span>{" "}
                                {detail.photograph_of_unsafe_act_comment ||
                                  detail.observation_photo_comment ||
                                  detail.photo_comment}
                              </div>
                            )}
                        </div>
                      ) : (
                        <p className="text-sm italic text-muted-foreground">
                          No photograph uploaded by checker
                        </p>
                      )}
                    </div>

                    <div className="rounded-xl border bg-card p-5 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Hazard Selected By Checker
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowRiskMatrix(true)}
                          className="text-xs font-bold bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-200 px-3 py-1.5 rounded-lg shadow-sm transition-colors cursor-pointer"
                        >
                          View Risk Matrix
                        </button>
                      </div>
                      {renderObservationHazards(detail.hazard_categories)}
                    </div>

                    <div className="rounded-xl border bg-card p-5 shadow-sm">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Risk Selected By Checker
                      </p>
                      {renderObservationRisk(detail.risk)}
                    </div>

                    <div className="rounded-xl border bg-card p-5 shadow-sm">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Target Date Set By Checker
                      </p>
                      <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-1.5 text-sm font-semibold text-foreground">
                        <Calendar className="h-4 w-4 text-orange-500" />
                        <span>
                          {formatObservationTargetDate(
                            detail.target_date ||
                            detail.targetDate ||
                            detail.observation_target_date
                          )}
                        </span>
                      </div>
                    </div>

                    <div className="rounded-xl border bg-card p-5 shadow-sm">
                      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Corrective Action / Preventive Action
                      </p>

                      <div className="space-y-4">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                            Corrective Action
                          </p>
                          {renderNumberedActionList(detail.corrective_action)}
                        </div>

                        <div className="border-t border-border/60 pt-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                            Preventive Action
                          </p>
                          {renderNumberedActionList(detail.preventive_action)}
                        </div>
                      </div>
                    </div>

                    {observationPreviousAttempts.length > 0 && (
                      <div className="rounded-xl border border-red-200 bg-red-50/30 p-5 shadow-sm relative overflow-hidden">
                        <div className="absolute left-0 top-0 h-full w-1 bg-red-500"></div>

                        <h3 className="mb-1 pl-2 font-bold text-red-700">
                          Reason For Rejection
                        </h3>

                        <p className="mb-4 pl-2 text-xs text-red-600">
                          Review your earlier submission, rejection reason,
                          Checker remarks and attached evidence before
                          submitting the next rectification.
                        </p>

                        <div className="space-y-4">
                          {observationPreviousAttempts.map((attempt) =>
                            renderObservationRejectedAttempt(attempt),
                          )}
                        </div>
                      </div>
                    )}

                    <div className="rounded-xl border bg-card p-5 shadow-sm">
                      <label className="mb-2 block text-sm font-semibold text-foreground">
                        Rectification Remarks
                      </label>
                      <NumberedTextArea
                        rows={4}
                        name="observationComment"
                        value={observationComment}
                        onChange={(e) => setObservationComment(e.target.value)}
                        className="w-full rounded-lg border border-border bg-muted/30 p-3 text-sm outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                        placeholder="Describe what was rectified / corrected..."
                      />

                      <div className="mt-4">
                        <label className="mb-2 block text-sm font-semibold text-foreground">
                          Attach Closure Photograph
                        </label>

                        {observationPhotos.length > 0 && (
                          <div className="mb-3 flex flex-wrap gap-3">
                            {observationPhotos.map((file, index) => (
                              <div
                                key={`${file.name}-${index}`}
                                className="relative"
                              >
                                <img
                                  src={URL.createObjectURL(file)}
                                  alt={`Corrective action ${index + 1}`}
                                  className="h-24 w-24 rounded-lg border border-border object-cover"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    setObservationPhotos((prev) =>
                                      prev.filter((_, i) => i !== index),
                                    )
                                  }
                                  className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white shadow"
                                >
                                  <XIcon className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        <PhotoUploadArea
                          id={`observation-maker-${detail.id}-${observationPhotos.length}`}
                          previewBase64={null}
                          label={
                            observationPhotos.length > 0
                              ? "Add another corrective-action photo"
                              : "Attach corrective-action photo"
                          }
                          onFileChange={(e) => {
                            const files = e.target.files;
                            if (!files || files.length === 0) return;

                            setObservationPhotos((prev) => [
                              ...prev,
                              ...Array.from(files),
                            ]);
                          }}
                          onRemove={() => { }}
                        />
                      </div>
                    </div>

                    <SafetyObservationSignOffSection
                      detail={detail}
                      className="mt-6 mb-6"
                    />

                    <button
                      type="button"
                      onClick={handleSubmitAllFixes}
                      disabled={submitting || signatureSubmitting}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-3.5 text-sm font-bold text-white shadow-lg shadow-orange-200 transition-all hover:shadow-xl hover:shadow-orange-300 disabled:opacity-60"
                    >
                      <Send className="h-4 w-4" />
                      {submitting || signatureSubmitting
                        ? "Submitting…"
                        : "Submit Rectification"}
                    </button>
                  </div>
                </section>
              ) : (
                <section>
                  <button
                    type="button"
                    onClick={backToDashboard}
                    className="mb-4 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back to Dashboard
                  </button>

                  <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm">
                    <h1 className="text-lg font-bold text-foreground sm:text-xl">
                      {detail.name}
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {makerEditableItems.length} item(s) to fix
                    </p>
                  </div>

                  <ReportHeaderInfoCard detail={detail} />

                  {makerEditableItems.length === 0 ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">
                      No items pending for you right now.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {makerFixItems.map(
                        ({ item, sub, editable, highlighted }, idx) => {
                          const hasAnswer = Boolean(
                            sub.answer ||
                            sub.latest_maker_answer ||
                            sub.latest_checker_answer,
                          );
                          const isHidden = !editable && !hasAnswer;

                          if (isHidden) return null;

                          const checkerComment =
                            sub.latest_checker_reject_remarks ||
                            sub.latest_checker_remarks ||
                            sub.checker_remarks ||
                            "";

                          const checkerPhoto =
                            sub.latest_checker_reject_photo_url ||
                            sub.latest_checker_photo_url ||
                            "";

                          return (
                            <div
                              key={item.id}
                              className={`rounded-xl border p-5 shadow-sm transition-shadow hover:shadow-md ${highlighted
                                  ? "border-red-300 bg-red-50"
                                  : "bg-card border-border opacity-75"
                                }`}
                            >
                              {highlighted ? (
                                <span className="inline-flex rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
                                  Rework Required
                                </span>
                              ) : editable ? (
                                <span className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                                  Pending Maker Input
                                </span>
                              ) : (
                                <span className="inline-flex rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">
                                  Filled by Checker
                                </span>
                              )}

                              <div className="mb-4 flex items-start gap-3">
                                <QuestionBadge number={idx + 1} />
                                <p className="pt-1 text-sm font-semibold text-foreground sm:text-base">
                                  {item.title}
                                </p>
                              </div>

                              <div className="space-y-3">
                                {sub.supervisor_remarks && (
                                  <RemarksBubble
                                    label="Supervisor"
                                    text={sub.supervisor_remarks}
                                  />
                                )}
                                {checkerPhoto && (
                                  <PhotoViewButton
                                    url={checkerPhoto}
                                    label="View Checker Photo"
                                  />
                                )}

                                {editable ? (
                                  <DynamicSafetyInput
                                    item={item}
                                    value={
                                      makerAnswers[sub.id]?.answer ??
                                      (typeof makerAnswers[sub.id] === "string"
                                        ? makerAnswers[sub.id]
                                        : "")
                                    }
                                    secondaryValue={
                                      makerAnswers[sub.id]?.secondary_answer ??
                                      ""
                                    }
                                    onChange={(value) => {
                                      setMakerAnswers((prev) => ({
                                        ...prev,
                                        [sub.id]: {
                                          ...prev[sub.id],
                                          answer: value,
                                        },
                                      }));
                                    }}
                                    onSecondaryChange={(value) => {
                                      setMakerAnswers((prev) => ({
                                        ...prev,
                                        [sub.id]: {
                                          ...prev[sub.id],
                                          secondary_answer: value,
                                        },
                                      }));
                                    }}
                                  />
                                ) : (
                                  <div className="flex flex-col gap-1.5">
                                    <RemarksBubble
                                      label="Submission Answer"
                                      text={
                                        sub.answer ||
                                        (typeof makerAnswers[sub.id] ===
                                          "object"
                                          ? makerAnswers[sub.id]?.answer
                                          : makerAnswers[sub.id]) ||
                                        "No Answer"
                                      }
                                    />
                                    {(sub.secondary_answer ||
                                      (typeof makerAnswers[sub.id] === "object"
                                        ? makerAnswers[sub.id]?.secondary_answer
                                        : null)) && (
                                        <RemarksBubble
                                          label="Additional Details"
                                          text={
                                            sub.secondary_answer ||
                                            (typeof makerAnswers[sub.id] ===
                                              "object"
                                              ? makerAnswers[sub.id]
                                                ?.secondary_answer
                                              : null)
                                          }
                                        />
                                      )}
                                  </div>
                                )}

                                {checkerComment && (
                                  <RemarksBubble
                                    label="Checker Comment"
                                    text={checkerComment}
                                  />
                                )}

                                <div>
                                  <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                                    Remarks (what was fixed / checked)
                                  </label>
                                  <textarea
                                    disabled={!editable}
                                    className={`w-full rounded-lg border border-border bg-muted/30 p-3 text-sm ${!editable
                                        ? "cursor-not-allowed opacity-60"
                                        : ""
                                      }`}
                                    value={makerRemarks[sub.id] ?? ""}
                                    onChange={(e) => {
                                      if (!editable) return;

                                      setMakerRemarks((prev) => ({
                                        ...prev,
                                        [sub.id]: e.target.value,
                                      }));
                                    }}
                                    rows={2}
                                  />
                                </div>

                                {editable && (
                                  <PhotoUploadArea
                                    id={`maker-fix-${sub.id}`}
                                    previewBase64={makerMedia[sub.id]}
                                    label={
                                      item.photo_required
                                        ? "Upload rework photo (required)"
                                        : "Upload rework photo (optional)"
                                    }
                                    onFileChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;

                                      setMakerMedia((prev) => ({
                                        ...prev,
                                        [sub.id]: file,
                                      }));
                                    }}
                                    onRemove={() =>
                                      setMakerMedia((prev) => ({
                                        ...prev,
                                        [sub.id]: undefined,
                                      }))
                                    }
                                  />
                                )}

                                {/* <div className="flex justify-end pt-1">
                                                            <button type="button"
                                                                onClick={() => handleSubmitSingleFix(sub.id)}
                                                                disabled={submitting}
                                                                className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-primary to-primary/90 px-4 py-2 text-xs font-semibold text-primary-foreground shadow-sm transition-all hover:shadow-md disabled:opacity-60">
                                                                <Send className="h-3.5 w-3.5" />
                                                                Submit Fix
                                                            </button>
                                                        </div> */}
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  )}

                  <SafetyChecklistSignOffSection
                    detail={detail}
                    className="mt-6"
                  />

                  {makerEditableItems.length > 0 && (
                    <div className="mt-6">
                      <button
                        type="button"
                        onClick={handleSubmitAllFixes}
                        disabled={submitting}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-3.5 text-sm font-bold text-white shadow-lg shadow-orange-200 transition-all hover:shadow-xl hover:shadow-orange-300 disabled:opacity-60"
                      >
                        <Send className="h-4 w-4" />
                        {submitting ? "Submitting…" : "Submit All Fixes"}
                      </button>
                    </div>
                  )}
                </section>
              ))}

            {/* ═══ VIEW: DASHBOARD ═══ */}
            {view === "dashboard" && (
              <div className="w-full space-y-6">
                <section className="space-y-6">
                  {/* Header Banner */}
                  <SafetyHeroBanner
                    type={isObservations ? "observation" : "checklist"}
                    categoryTag={isObservations ? "HAZARD & OBSERVATION TRACKING" : "SAFETY ASSURANCE"}
                    categoryIcon={isObservations ? TriangleAlert : ShieldCheck}
                    title={isObservations ? "Safety Observations" : "Safety Checklists"}
                    subtitle={
                      isObservations
                        ? "Site safety observations & hazard rectification — identify risks, take action, and maintain safety standards."
                        : "Safety checklist management & rectifications — verify protocols and track corrective measures."
                    }
                  >
                    {!isObservations && (
                      <button
                        type="button"
                        onClick={openCreateChecklist}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2.5 text-xs sm:text-sm font-semibold text-white shadow-sm hover:shadow-md hover:from-orange-600 hover:to-orange-700 transition-all cursor-pointer"
                      >
                        <Plus className="h-4 w-4" />
                        Start Checklist
                      </button>
                    )}
                  </SafetyHeroBanner>

                  {/* Counter cards */}
                  <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-5">
                    {[
                      {
                        label: "Pending Reviews",
                        icon: <Clock className="h-4 w-4" />,
                        color: "hsl(45, 93%, 47%)",
                        count: makerPendingReview.length,
                      },
                      {
                        label: "Pending Rectifications",
                        icon: <Eye className="h-4 w-4" />,
                        color: "hsl(24.6, 95%, 53.1%)",
                        count: makerPendingRectification.length,
                      },
                      {
                        label: "Approved",
                        icon: <CheckCircle className="h-4 w-4" />,
                        color: "hsl(145, 65%, 42%)",
                        count: makerApprovedCount,
                      },
                      {
                        label: "Rejected",
                        icon: <TriangleAlert className="h-4 w-4" />,
                        color: "hsl(0, 75%, 55%)",
                        count: makerRejectedCount,
                      },
                      {
                        label: "Total",
                        icon: <ClipboardList className="h-4 w-4" />,
                        color: "hsl(210, 100%, 50%)",
                        count: makerTotalCount,
                      },
                    ].map(({ label, icon, color, count }) => (
                      <div
                        key={label}
                        className="group relative flex flex-col justify-between h-full rounded-2xl border border-slate-200/90 bg-card p-3.5 sm:p-4 shadow-2xs transition-all duration-200 select-none hover:-translate-y-0.5 hover:shadow-md hover:border-slate-300"
                      >
                        <div className="flex items-start gap-2.5 min-h-[38px]">
                          <span
                            className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors"
                            style={{
                              backgroundColor: `${color}18`,
                              color: color,
                            }}
                          >
                            {icon}
                          </span>
                          <span
                            className="text-xs sm:text-[13px] font-semibold leading-snug line-clamp-2"
                            style={{ color: color }}
                          >
                            {label}
                          </span>
                        </div>
                        <div className="mt-3 flex items-baseline border-t border-border/40 pt-2.5">
                          <span className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
                            {Number.isFinite(Number(count)) ? Number(count) : 0}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Active Queues: Side-by-side 2-column grid */}
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-6">
                    {/* Column 1: Pending for Review */}
                    <div className="flex flex-col">
                      <div className="mb-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-yellow-500/15 text-yellow-600">
                            <Clock className="h-4 w-4" />
                          </div>
                          <h2 className="text-sm font-semibold text-yellow-700 sm:text-base">
                            Pending for Review
                          </h2>
                          <span className="rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-800">
                            {makerPendingReview.length}
                          </span>
                        </div>
                      </div>

                      <div
                        className="rounded-2xl border border-slate-200/90 bg-card shadow-2xs overflow-y-auto max-h-[460px] custom-scrollbar divide-y divide-border/50"
                      >
                        {makerPendingReview.length === 0 ? (
                          <div className="flex h-36 items-center justify-center p-6 text-center text-sm text-muted-foreground">
                            No items pending review
                          </div>
                        ) : (
                          (isObservations ? paginatedMakerPendingReview : makerPendingReview).map((item) => (
                            <div
                              key={item.id}
                              className="flex flex-col gap-2.5 p-4 transition-colors hover:bg-slate-50/70 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-foreground text-sm sm:text-base leading-snug">
                                  {getChecklistDisplayName(item)}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {getObservationDisplayDateTime(item)}
                                  {totalQuestionsLabel(item)
                                    ? ` · ${totalQuestionsLabel(item)}`
                                    : ""}
                                </p>
                                <ChecklistListMeta item={item} />
                              </div>

                              <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                                <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-1 text-xs font-semibold text-yellow-700">
                                  In Review
                                </span>

                                <button
                                  type="button"
                                  onClick={() => {
                                    setReadonlyChecklist(item);
                                    setReadonlyPendingStatusLabel(
                                      "Pending for Review",
                                    );
                                  }}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100 cursor-pointer"
                                  title="View Details"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      {isObservations && totalReviewPages > 1 && (
                        <div className="mt-2.5 flex items-center justify-between px-1 text-xs text-muted-foreground">
                          <span>
                            Page {currentReviewPage} of {totalReviewPages} ({totalReviewCount} total)
                          </span>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setReviewPage((prev) => Math.max(1, prev - 1))}
                              disabled={currentReviewPage <= 1}
                              className="rounded-lg border border-border bg-white px-2.5 py-1 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <span className="inline-flex min-w-6 items-center justify-center rounded bg-orange-50 px-2 py-0.5 text-xs font-bold text-orange-600">
                              {currentReviewPage}
                            </span>
                            <button
                              type="button"
                              onClick={() => setReviewPage((prev) => Math.min(totalReviewPages, prev + 1))}
                              disabled={currentReviewPage >= totalReviewPages}
                              className="rounded-lg border border-border bg-white px-2.5 py-1 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Column 2: Pending for Rectification */}
                    <div className="flex flex-col">
                      <div className="mb-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-orange-500/15 text-orange-600">
                            <Send className="h-4 w-4" />
                          </div>
                          <h2 className="text-sm font-semibold text-orange-700 sm:text-base">
                            Pending for Rectification
                          </h2>
                          <span className="rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-semibold text-orange-800">
                            {makerPendingRectification.length}
                          </span>
                        </div>
                      </div>

                      <div
                        className="rounded-2xl border border-slate-200/90 bg-card shadow-2xs overflow-y-auto max-h-[460px] custom-scrollbar divide-y divide-border/50"
                      >
                        {makerPendingRectification.length === 0 ? (
                          <div className="flex h-36 items-center justify-center p-6 text-center text-sm text-muted-foreground">
                            No items pending rectification
                          </div>
                        ) : (
                          (isObservations ? paginatedMakerPendingRectification : makerPendingRectification).map((item) => {
                            const canRectify = makerCanRectify(item);

                            return (
                              <div
                                key={item.id}
                                className="flex flex-col gap-2.5 p-4 transition-colors hover:bg-slate-50/70 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="font-semibold text-foreground text-sm sm:text-base leading-snug">
                                    {getChecklistDisplayName(item)}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {getObservationDisplayDateTime(item)}
                                    {rejectedPointsLabel(item)
                                      ? ` · ${rejectedPointsLabel(item)}`
                                      : ""}
                                  </p>
                                  <ChecklistListMeta item={item} />
                                </div>

                                <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                                  <span
                                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${canRectify
                                        ? "bg-red-100 text-red-700"
                                        : "bg-orange-100 text-orange-700"
                                      }`}
                                  >
                                    {canRectify
                                      ? "Rectification Pending"
                                      : "Rectifying"}
                                  </span>

                                  {canRectify ? (
                                    <button
                                      type="button"
                                      onClick={() => openMakerFix(item)}
                                      className="rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-2xs transition-all hover:shadow-md cursor-pointer"
                                    >
                                      Rectify now
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setReadonlyChecklist(item);
                                        setReadonlyPendingStatusLabel(
                                          "Pending for Rectification",
                                        );
                                      }}
                                      className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100 cursor-pointer"
                                      title="View Details"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </button>
                                  )}


                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                      {isObservations && totalRectificationPages > 1 && (
                        <div className="mt-2.5 flex items-center justify-between px-1 text-xs text-muted-foreground">
                          <span>
                            Page {currentRectificationPage} of {totalRectificationPages} ({totalRectificationCount} total)
                          </span>
                          <div className="flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setRectificationPage((prev) => Math.max(1, prev - 1))}
                              disabled={currentRectificationPage <= 1}
                              className="rounded-lg border border-border bg-white px-2.5 py-1 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <span className="inline-flex min-w-6 items-center justify-center rounded bg-orange-50 px-2 py-0.5 text-xs font-bold text-orange-600">
                              {currentRectificationPage}
                            </span>
                            <button
                              type="button"
                              onClick={() => setRectificationPage((prev) => Math.min(totalRectificationPages, prev + 1))}
                              disabled={currentRectificationPage >= totalRectificationPages}
                              className="rounded-lg border border-border bg-white px-2.5 py-1 text-xs font-semibold text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <ChecklistFilters
                    filters={filters}
                    setFilters={setFilters}
                    contractorOptions={contractorOptions}
                    typeOptions={typeOptions}
                    checkerOptions={checkerOptions}
                    checkerLoading={checkerLoading}
                  />

                  {/* Completed */}
                  {isObservations ? (
                    <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
                      {/* Left: Closed Observations */}
                      <div className="flex flex-col">
                        <div className="mb-2.5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-600">
                              <CheckCircle className="h-4 w-4" />
                            </div>
                            <h2 className="text-sm font-semibold text-emerald-700 sm:text-base">
                              Closed Observations
                            </h2>
                            <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                              {makerCompletedChecklists.length}
                            </span>
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200/90 bg-card shadow-2xs overflow-y-auto max-h-[420px] custom-scrollbar divide-y divide-border/50">
                          {makerCompletedChecklists.length === 0 ? (
                            <div className="flex h-36 items-center justify-center p-6 text-center text-sm text-muted-foreground">
                              No closed observations
                            </div>
                          ) : (
                            makerCompletedChecklists.map((item) => (
                              <div
                                key={item.id}
                                className="flex flex-col gap-2.5 p-4 transition-colors hover:bg-slate-50/70 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="font-semibold text-foreground text-sm sm:text-base leading-snug">
                                    {getChecklistDisplayName(item)}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {getObservationDisplayDateTime(item)}
                                    {totalQuestionsLabel(item)
                                      ? ` · ${totalQuestionsLabel(item)}`
                                      : ""}
                                  </p>
                                  <ChecklistListMeta item={item} />
                                </div>

                                <div className="flex shrink-0 flex-wrap items-center gap-3 sm:justify-end">
                                  <span
                                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${isClosedRejected(item)
                                        ? "bg-red-100 text-red-700"
                                        : "bg-green-100 text-green-700"
                                      }`}
                                  >
                                    {isClosedRejected(item)
                                      ? "Rejected"
                                      : "Approved"}
                                  </span>

                                  <button
                                    type="button"
                                    title="View History"
                                    onClick={() => {
                                      setReadonlyChecklist(item);
                                      setReadonlyPendingStatusLabel(
                                        "Closed Observation",
                                      );
                                    }}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100 cursor-pointer"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>

                                  <button
                                    type="button"
                                    title="Download Report"
                                    onClick={() => handleDownloadReport(item)}
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-green-200 bg-green-50 text-green-700 transition-colors hover:bg-green-100 cursor-pointer"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Right: Reports */}
                      <div className="flex flex-col">
                        <div className="mb-2.5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-600">
                              <CheckCircle className="h-4 w-4" />
                            </div>
                            <h2 className="text-sm font-semibold text-emerald-700 sm:text-base">
                              Reports
                            </h2>
                            <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                              {makerGroupedReports.length}
                            </span>
                          </div>
                        </div>
                        <div className="rounded-2xl border border-slate-200/90 bg-card shadow-2xs overflow-y-auto max-h-[420px] custom-scrollbar divide-y divide-border/50">
                          {makerGroupedReports.length === 0 ? (
                            <div className="flex h-36 items-center justify-center p-6 text-center text-sm text-muted-foreground">
                              No reports generated
                            </div>
                          ) : (
                            makerGroupedReports.map((report) => (
                              <div
                                key={
                                  report.key ||
                                  `report-${report.reportBatchId || report.reportNumber || report.date}`
                                }
                                className="flex flex-col gap-2.5 p-4 transition-colors hover:bg-slate-50/70 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <div className="min-w-0 flex-1">
                                  <p className="font-semibold text-foreground text-sm sm:text-base leading-snug">
                                    {report.reportName}
                                  </p>
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {report.displayDate || report.reportDate}
                                    {report.items?.length > 0 ? (
                                      <span className="ml-1.5 text-xs text-slate-500 font-normal">
                                        · {report.items.length} Observation
                                        {report.items.length === 1 ? "" : "s"}
                                      </span>
                                    ) : null}
                                  </p>
                                </div>
                                <div className="flex shrink-0 flex-wrap items-center gap-3 sm:justify-end">
                                  <button
                                    type="button"
                                    title="Download Report"
                                    onClick={() =>
                                      handleDownloadObservationReport(report)
                                    }
                                    className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-green-200 bg-green-50 text-green-700 transition-colors hover:bg-green-100 cursor-pointer"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6 flex flex-col">
                      <div className="mb-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-600">
                            <CheckCircle className="h-4 w-4" />
                          </div>
                          <h2 className="text-sm font-semibold text-emerald-700 sm:text-base">
                            Completed Checklists
                          </h2>
                          <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                            {makerCompletedChecklists.length}
                          </span>
                        </div>
                      </div>
                      <div className="rounded-2xl border border-slate-200/90 bg-card shadow-2xs overflow-y-auto max-h-[420px] custom-scrollbar divide-y divide-border/50">
                        {makerCompletedChecklists.length === 0 ? (
                          <div className="flex h-36 items-center justify-center p-6 text-center text-sm text-muted-foreground">
                            No completed items
                          </div>
                        ) : (
                          makerCompletedChecklists.map((item) => (
                            <div
                              key={item.id}
                              className="flex flex-col gap-2.5 p-4 transition-colors hover:bg-slate-50/70 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-foreground text-sm sm:text-base leading-snug">
                                  {getChecklistDisplayName(item)}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {getObservationDisplayDateTime(item)}
                                  {totalQuestionsLabel(item)
                                    ? ` · ${totalQuestionsLabel(item)}`
                                    : ""}
                                </p>
                                <ChecklistListMeta item={item} />
                              </div>

                              <div className="flex shrink-0 flex-wrap items-center gap-3 sm:justify-end">
                                <span
                                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${isClosedRejected(item)
                                      ? "bg-red-100 text-red-700"
                                      : "bg-green-100 text-green-700"
                                    }`}
                                >
                                  {isClosedRejected(item)
                                    ? "Rejected"
                                    : "Approved"}
                                </span>

                                <button
                                  type="button"
                                  title="View History"
                                  onClick={() => {
                                    setHistoryChecklist(item);
                                  }}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-blue-700 transition-colors hover:bg-blue-100 cursor-pointer"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>

                                <button
                                  type="button"
                                  title="Download Report"
                                  onClick={() => handleDownloadReport(item)}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-green-200 bg-green-50 text-green-700 transition-colors hover:bg-green-100 cursor-pointer"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {/* Safety Observation Pagination */}
                  {isObservations && (observationPagination.total_pages > 1 || observationPagination.count > 0) && (
                    <div className="mb-6 flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-xs text-muted-foreground sm:text-sm">
                        Showing Pending for Rectification page {observationPagination.current_page} of {observationPagination.total_pages}
                        {observationPagination.count > 0
                          ? ` • ${observationPagination.count} total observations`
                          : ""}
                      </p>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            setRectificationPage((prev) => Math.max(1, prev - 1))
                          }
                          disabled={
                            loading ||
                            observationPagination.current_page <= 1
                          }
                          className="rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Previous
                        </button>

                        <span className="inline-flex min-w-8 items-center justify-center rounded-lg bg-orange-50 px-2.5 py-1.5 text-xs font-semibold text-orange-600">
                          {observationPagination.current_page}
                        </span>

                        <button
                          type="button"
                          onClick={() =>
                            setRectificationPage((prev) =>
                              Math.min(observationPagination.total_pages, prev + 1),
                            )
                          }
                          disabled={
                            loading ||
                            observationPagination.current_page >=
                              observationPagination.total_pages
                          }
                          className="rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              </div>
            )}
          </>
        )}

        {isObservations ? (
          <SafetyObservationReadonlyModal
            open={!!readonlyChecklist}
            checklistId={readonlyChecklist?.id}
            pendingStatusLabel={readonlyPendingStatusLabel}
            onClose={() => setReadonlyChecklist(null)}
          />
        ) : (
          <SafetyChecklistReadonlyModal
            open={!!readonlyChecklist}
            checklistId={readonlyChecklist?.id}
            pendingStatusLabel={readonlyPendingStatusLabel}
            onClose={() => setReadonlyChecklist(null)}
          />
        )}

        <SafetyChecklistHistoryModal
          open={!!historyChecklist}
          checklistId={historyChecklist?.id}
          title={historyChecklist?.name}
          onClose={() => setHistoryChecklist(null)}
        />

        {isMakerSignatureModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-2xl rounded-xl bg-white p-5 shadow-xl">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-base font-semibold text-foreground">
                  Final Signature
                </h3>
                <button
                  type="button"
                  onClick={handleCloseMakerSignatureModal}
                  className="rounded p-1 text-muted-foreground hover:bg-muted"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                {isObservations
                  ? "Draw your signature and submit the corrective action."
                  : "Draw your signature and submit to complete the checklist action."}
              </p>
              <div className="rounded-lg border border-border bg-white p-2">
                <SafetyResponsiveSignatureCanvas ref={sigCanvasRef} penColor="black" />
              </div>
              <div className="mt-3 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => sigCanvasRef.current?.clear()}
                  className="w-full rounded-lg border border-slate-200 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={handleMakerSignatureSubmit}
                  disabled={signatureSubmitting}
                  className="w-full rounded-lg bg-gradient-to-r from-orange-400 to-orange-500 py-2.5 text-xs font-semibold text-white shadow-sm hover:from-orange-500 hover:to-orange-600 disabled:opacity-60 transition-all"
                >
                  {signatureSubmitting
                    ? "Submitting..."
                    : signaturePurpose === "create"
                      ? "Submit Checklist"
                      : "Submit Rectification"}
                </button>
              </div>
            </div>
          </div>
        )}
        <RiskMatrixModal
          open={showRiskMatrix}
          onClose={() => setShowRiskMatrix(false)}
        />
      </div>
    </div>
  );
}
